/**
 * Component Generators - Generate TSX code strings for each CraftJS component type
 *
 * Each generator takes the component's props and returns a TSX code string.
 * For interactive components, the generated code includes useState/useEffect/useRef hooks.
 */

import { ImageAsset, getImageReference } from "./imageScanner";
import {
  containerMaxWidths,
  justifyContentMap,
  alignItemsMap,
  shadows,
  getButtonVariantStyles,
  getButtonSizeStyles,
  heroStyles,
  sliderStyles,
  beforeAfterStyles,
  faqStyles,
  navbarStyles,
  footerStyles,
  testimonialStyles,
  contactFormStyles,
  videoStyles,
  serviceCardStyles,
} from "../../config/styleConfig";

// ─────────────────────────────────────────────
// Helper Utilities
// ─────────────────────────────────────────────

const esc = (s: unknown): string => {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/`/g, "\\`")
    .replace(/\$/g, "\\$");
};

const escJsx = (s: unknown): string => {
  if (s === null || s === undefined) return "";
  // Auto-resolve multilingual text objects {th:"...",en:"..."} to Thai (default)
  if (typeof s === "object" && !Array.isArray(s)) {
    const keys = Object.keys(s as Record<string, unknown>);
    if (keys.length > 0 && keys.every((k) => ["th", "en", "zh"].includes(k))) {
      const obj = s as Record<string, string>;
      return escJsx(obj.th || obj.en || "");
    }
  }
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/{/g, "&#123;")
    .replace(/}/g, "&#125;");
};

const escQuotes = (s: unknown): string => {
  if (s === null || s === undefined) return "";
  return String(s).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
};

const cssObj = (style: Record<string, unknown>): string => {
  const entries = Object.entries(style).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (entries.length === 0) return "{}";
  const inner = entries
    .map(([k, v]) => {
      if (typeof v === "number") return `${k}: ${v}`;
      return `${k}: "${escQuotes(v)}"`;
    })
    .join(", ");
  return `{ ${inner} }`;
};

const imgRef = (url: string, assets: ImageAsset[]): string => {
  const ref = getImageReference(url, assets);
  if (ref.type === "import") return `{${ref.value}}`;
  return `"${esc(ref.value)}"`;
};

const safeArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

type Props = Record<string, unknown>;

// ─────────────────────────────────────────────
// Simple Components (inline, no hooks needed)
// ─────────────────────────────────────────────

/** Resolve multilingual text for code generation.
 *  Returns plain string for single-lang, or the default lang value for multilingual. */
const resolveText = (text: unknown): string => {
  if (typeof text === "object" && text !== null) {
    const obj = text as Record<string, string>;
    return obj.th || obj.en || "";
  }
  return String(text ?? "");
};

/** Generate JSX expression for multilingual text (runtime switching).
 *  Returns `{lang === "en" ? "English" : "Thai"}` for object text, or plain escaped string. */
const resolveTextJsx = (text: unknown): string => {
  if (typeof text === "object" && text !== null) {
    const obj = text as Record<string, string>;
    const th = escJsx(obj.th || "");
    const en = escJsx(obj.en || "");
    if (th && en && th !== en) {
      return `{lang === "en" ? "${en}" : "${th}"}`;
    }
    return escJsx(th || en || "");
  }
  return escJsx(String(text ?? ""));
};

/** Return fontFamily CSS fragment for non-Thai text: `, fontFamily: "var(--font-family-en, ...)"` or "" (with leading comma, for appending to existing styles) */
const fontENStyle = (text: unknown, overrideFont?: unknown): string => {
  if (overrideFont) return `, fontFamily: "'${esc(String(overrideFont))}', var(--font-family), sans-serif"`;
  const resolved = resolveText(text);
  if (!resolved || /[\u0E00-\u0E7F]/.test(resolved)) return "";
  return `, fontFamily: "var(--font-family-en, var(--font-family))"`;
};

/** Same as fontENStyle but without leading comma (for standalone style objects) */
const fontENProp = (text: unknown): string => {
  const resolved = resolveText(text);
  if (!resolved || /[\u0E00-\u0E7F]/.test(resolved)) return "";
  return `fontFamily: "var(--font-family-en, var(--font-family))"`;
};

export const generateText = (props: Props): string => {
  const {
    text: rawText = "Text",
    fontSize = 16,
    fontFamily,
    fontWeight = "normal",
    fontStyle = "normal",
    textAlign = "left",
    color = "#000000",
    tagName = "p",
    lineHeight = 1.5,
    marginTop = 0,
    marginBottom = 0,
    marginLeft = 0,
    marginRight = 0,
    whiteSpace,
    maxWidth,
  } = props;

  const Tag = tagName as string;
  const colorStr = String(color || "#000000");
  const isGradientColor = colorStr.includes("gradient");

  const resolvedText = resolveText(rawText);
  const hasThai = /[\u0E00-\u0E7F]/.test(resolvedText);

  const style: Record<string, unknown> = {
    fontSize: `${fontSize}px`,
    ...(fontFamily
      ? { fontFamily: `'${fontFamily}', var(--font-family), sans-serif` }
      : !hasThai ? { fontFamily: "var(--font-family-en, var(--font-family))" } : {}),
    fontWeight: fontWeight === "normal" ? 400 : fontWeight === "medium" ? 500 : fontWeight === "semibold" ? 600 : fontWeight === "bold" ? 700 : fontWeight,
    fontStyle,
    textAlign,
    lineHeight,
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    marginLeft: `${marginLeft}px`,
    marginRight: `${marginRight}px`,
    wordBreak: "break-word" as const,
    overflowWrap: "break-word" as const,
  };

  if (isGradientColor) {
    style.background = colorStr;
    style.WebkitBackgroundClip = "text";
    style.WebkitTextFillColor = "transparent";
    if (fontStyle === "italic") style.paddingRight = "0.25em";
  } else {
    style.color = colorStr;
  }
  if (whiteSpace) style.whiteSpace = whiteSpace;
  if (maxWidth) style.maxWidth = typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;

  // Handle HTML content in text
  const text = resolveText(rawText);
  const textContent = String(text || "");
  const hasHtml = /<[^>]+>/.test(textContent);

  // Per-element mobile font size (driven by mobileFontSize prop, not hardcoded selectors)
  let styleStr = cssObj(style);
  let clsAttr = "";
  const mFs = props.mobileFontSize as number | undefined;
  if (mFs != null) {
    styleStr = styleStr.replace(/\s*\}$/, `, "--m-fs": "${mFs}px" }`);
    clsAttr = ` className="mobile-fs"`;
  }

  if (hasHtml) {
    return `<${Tag} style={${styleStr}}${clsAttr} dangerouslySetInnerHTML={{ __html: \`${esc(textContent)}\` }} />`;
  }
  // Use runtime lang switching for multilingual text
  const jsxText = resolveTextJsx(rawText);
  return `<${Tag} style={${styleStr}}${clsAttr}>${jsxText}</${Tag}>`;
};

export const generateImage = (props: Props, assets: ImageAsset[]): string => {
  const {
    src = "",
    alt = "Image",
    width = "100%",
    height = "auto",
    borderRadius = 0,
    objectFit = "cover",
    link,
    linkTarget = "_self",
    align = "left",
  } = props;

  if (!src) {
    return `<div style={{ width: "100%", height: "200px", backgroundColor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "${borderRadius}px", border: "2px dashed #d1d5db" }}><span style={{ color: "#9ca3af" }}>No image</span></div>`;
  }

  const style: Record<string, unknown> = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
    borderRadius: `${borderRadius}px`,
    objectFit,
    display: "block",
  };

  const containerStyle: Record<string, unknown> = {
    textAlign: align,
    width: "100%",
  };

  const imgTag = `<img src=${imgRef(src as string, assets)} alt="${esc(alt)}" style={${cssObj(style)}} />`;

  if (link) {
    return `<div style={${cssObj(containerStyle)}}><a href="${esc(link)}" target="${linkTarget}" rel="noopener noreferrer">${imgTag}</a></div>`;
  }
  return `<div style={${cssObj(containerStyle)}}>${imgTag}</div>`;
};

export const generateButton = (props: Props): string => {
  const {
    text: rawBtnText = "Button",
    link = "#",
    linkType = "external",
    variant = "primary",
    size = "md",
    borderRadius = 8,
    fullWidth = false,
    fontFamily,
    backgroundColor,
    textColor,
    borderColor,
    borderWidth,
    target = "_blank",
    hideOnDesktop = false,
    hideOnMobile = false,
  } = props;

  const sizeStyles = getButtonSizeStyles(size as "sm" | "md" | "lg", false);
  const variantStyles = getButtonVariantStyles(
    variant as "primary" | "secondary" | "outline" | "ghost",
    "var(--color-primary, #3b82f6)",
    "var(--color-secondary, #10b981)",
    backgroundColor as string | undefined,
    textColor as string | undefined,
  );

  const text = resolveText(rawBtnText);

  let href = link as string;
  if (linkType === "phone") href = `tel:${(link as string).replace(/[^\d+]/g, "")}`;
  else if (linkType === "email") href = `mailto:${link}`;

  const style: Record<string, unknown> = {
    display: fullWidth ? "block" : "inline-block",
    width: fullWidth ? "100%" : "auto",
    padding: `${sizeStyles.py}px ${sizeStyles.px}px`,
    fontSize: `${sizeStyles.fontSize}px`,
    ...(fontFamily
      ? { fontFamily: `'${fontFamily}', var(--font-family), sans-serif` }
      : !/[\u0E00-\u0E7F]/.test(text) ? { fontFamily: "var(--font-family-en, var(--font-family))" } : {}),
    fontWeight: 500,
    borderRadius: `${borderRadius}px`,
    background: variantStyles.background,
    color: variantStyles.color,
    border: variantStyles.border,
    textDecoration: "none",
    textAlign: "center" as const,
    cursor: "pointer",
    transition: "opacity 0.2s ease",
    boxSizing: "border-box" as const,
  };

  if (borderColor || borderWidth) {
    delete style.border;
    if (borderColor) style.borderColor = borderColor;
    style.borderWidth = borderWidth ? `${borderWidth}px` : "1px";
    style.borderStyle = "solid";
  }

  const dataAttrs: string[] = [];
  if (hideOnDesktop) dataAttrs.push(`data-hide-on-desktop="true"`);
  if (hideOnMobile) dataAttrs.push(`data-hide-on-mobile="true"`);

  const targetAttr = linkType === "external" ? `target="${target}" rel="noopener noreferrer"` : "";
  const isAnchor = (href as string).startsWith("#");
  const isExternal = (href as string).startsWith("http://") || (href as string).startsWith("https://");
  const onClickAttr = isAnchor
    ? `onClick={(e) => { e.preventDefault(); const el = document.getElementById("${esc((href as string).slice(1))}"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}`
    : isExternal
      ? ""
      : `onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate("${esc(href)}"); } }}`;
  return `<a href="${esc(href)}" ${onClickAttr} ${targetAttr} style={${cssObj(style)}} ${dataAttrs.join(" ")}>${resolveTextJsx(rawBtnText)}</a>`;
};

export const generateDivider = (props: Props): string => {
  const {
    height = 40,
    showLine = true,
    lineColor = "#e5e7eb",
    lineWidth = "full",
    lineStyle = "solid",
    lineThickness = "regular",
    backgroundColor = "transparent",
  } = props;

  const widthMap: Record<string, string> = { full: "100%", half: "50%", third: "33.33%" };
  const thicknessMap: Record<string, number> = { regular: 1, medium: 2, semibold: 3, bold: 4 };

  const containerStyle: Record<string, unknown> = {
    height: `${height}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    backgroundColor,
  };

  if (!showLine) {
    return `<div style={${cssObj(containerStyle)}} />`;
  }

  const lineStyleObj: Record<string, unknown> = {
    width: widthMap[lineWidth as string] || "100%",
    height: lineStyle === "solid" ? `${thicknessMap[lineThickness as string] || 1}px` : "0px",
    backgroundColor: lineStyle === "solid" ? lineColor : "transparent",
    borderTop: lineStyle !== "solid" ? `${thicknessMap[lineThickness as string] || 1}px ${lineStyle} ${lineColor}` : "none",
  };

  return `<div style={${cssObj(containerStyle)}}><div style={${cssObj(lineStyleObj)}} /></div>`;
};

export const generateVideo = (props: Props): string => {
  const {
    videoUrl = "",
    aspectRatio = "16:9",
    borderRadius = 8,
    autoplay = false,
    muted = true,
    loop = false,
  } = props;

  if (!videoUrl) {
    return `<div style={{ width: "100%", paddingTop: "${videoStyles.aspectRatios[aspectRatio as keyof typeof videoStyles.aspectRatios] || "56.25%"}", backgroundColor: "#f3f4f6", borderRadius: "${borderRadius}px", position: "relative" }}><div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No video URL</div></div>`;
  }

  // Convert YouTube/Vimeo to embed
  let embedUrl = videoUrl as string;
  const youtubeMatch = embedUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]+)/);
  if (youtubeMatch) embedUrl = `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  const vimeoMatch = embedUrl.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  const params: string[] = [];
  if (autoplay) params.push("autoplay=1");
  if (muted) params.push("mute=1");
  if (loop) params.push("loop=1");
  if (params.length > 0) embedUrl += `?${params.join("&")}`;

  const paddingTop = videoStyles.aspectRatios[aspectRatio as keyof typeof videoStyles.aspectRatios] || "56.25%";

  return `<div style={{ position: "relative", width: "100%", paddingTop: "${paddingTop}", borderRadius: "${borderRadius}px", overflow: "hidden" }}>
  <iframe
    src="${esc(embedUrl)}"
    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    allowFullScreen
  />
</div>`;
};

export const generateGoogleMap = (props: Props): string => {
  const {
    embedUrl = "",
    address = "",
    height = 400,
    borderRadius = 8,
    width = 100,
    align = "center",
  } = props;

  // Also check mapUrl for backwards compatibility
  const url = (embedUrl || props.mapUrl || "") as string;

  const containerStyle: Record<string, unknown> = {
    width: `${width}%`,
    margin: align === "center" ? "0 auto" : align === "right" ? "0 0 0 auto" : "0",
  };

  if (!url) {
    return `<div style={${cssObj({ ...containerStyle, height: `${height}px`, backgroundColor: "#f3f4f6", borderRadius: `${borderRadius}px`, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" })}}>No map URL provided</div>`;
  }

  let content = "";
  if (address) {
    content += `<p style={{ margin: "0 0 8px 0", fontSize: "0.875rem", color: "#4b5563" }}>{"\ud83d\udccd"} ${escJsx(address)}</p>\n`;
  }
  content += `<iframe
    src="${esc(url)}"
    style={{ width: "100%", height: "${height}px", border: "none", borderRadius: "${borderRadius}px" }}
    loading="lazy"
    allowFullScreen
  />`;

  return `<div style={${cssObj(containerStyle)}}>${content}</div>`;
};

export const generateSocialLinks = (props: Props): string => {
  const {
    links = [],
    iconSize = 40,
    gap = 12,
    style = "icons",
    alignment = "center",
  } = props;

  const safeLinks = safeArray(links);
  if (safeLinks.length === 0) return `{/* No social links configured */}`;

  const containerStyle: Record<string, unknown> = {
    display: "flex",
    flexWrap: "wrap",
    gap: `${gap}px`,
    justifyContent: alignment === "left" ? "flex-start" : alignment === "right" ? "flex-end" : "center",
    alignItems: "center",
  };

  const linkItems = safeLinks
    .map((item) => {
      const link = item as Record<string, unknown>;
      const platform = (link.platform || "") as string;
      const url = (link.url || "#") as string;
      if (!url || url === "#") return "";

      const platformColors: Record<string, string> = {
        facebook: "#1877F2",
        instagram: "#E4405F",
        line: "#06C755",
        tiktok: "#000000",
        youtube: "#FF0000",
        twitter: "#1DA1F2",
        linkedin: "#0A66C2",
      };

      const linkStyle: Record<string, unknown> =
        style === "icons"
          ? {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: `${iconSize}px`,
              height: `${iconSize}px`,
              borderRadius: "50%",
              backgroundColor: platformColors[platform] || "#6b7280",
              color: "#ffffff",
              textDecoration: "none",
              fontSize: `${Math.round(Number(iconSize) * 0.5)}px`,
              fontWeight: 700,
            }
          : style === "buttons"
            ? {
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                borderRadius: "8px",
                backgroundColor: platformColors[platform] || "#6b7280",
                color: "#ffffff",
                textDecoration: "none",
                fontSize: "14px",
                fontWeight: 500,
              }
            : {
                color: platformColors[platform] || "#6b7280",
                textDecoration: "none",
                fontSize: "14px",
              };

      const label = platform.charAt(0).toUpperCase() + platform.slice(1);
      const displayText = style === "icons" ? label.charAt(0) : label;

      return `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer" style={${cssObj(linkStyle)}}>${displayText}</a>`;
    })
    .filter(Boolean);

  return `<div style={${cssObj(containerStyle)}}>\n  ${linkItems.join("\n  ")}\n</div>`;
};

export const generateContactInfo = (props: Props): string => {
  const {
    title = "",
    description = "",
    email = "",
    phone = "",
    address = "",
    workingHoursText = "",
    layout = "vertical",
    backgroundColor = "#f9fafb",
    textColor = "#1f2937",
    borderRadius = 12,
    padding = 32,
    mapUrl = "",
    showMap = false,
  } = props;

  const containerStyle: Record<string, unknown> = {
    backgroundColor: backgroundColor as string,
    color: textColor as string,
    borderRadius: `${borderRadius}px`,
    padding: `${padding}px`,
  };

  const items: string[] = [];
  if (title) items.push(`<h3 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "16px" }}>${escJsx(title)}</h3>`);
  if (description) items.push(`<p style={{ fontSize: "1rem", color: "#6b7280", marginBottom: "24px" }}>${escJsx(description)}</p>`);

  const contactItems: string[] = [];
  if (phone) contactItems.push(`<div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}><span>{"\ud83d\udcde"}</span><a href="tel:${esc((phone as string).replace(/[^\d+]/g, ""))}" style={{ color: "var(--color-primary, #3b82f6)", textDecoration: "none" }}>${escJsx(phone)}</a></div>`);
  if (email) contactItems.push(`<div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}><span>{"\ud83d\udce7"}</span><a href="mailto:${esc(email)}" style={{ color: "var(--color-primary, #3b82f6)", textDecoration: "none" }}>${escJsx(email)}</a></div>`);
  if (address) contactItems.push(`<div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}><span>{"\ud83d\udccd"}</span><span>${escJsx(address)}</span></div>`);
  if (workingHoursText) contactItems.push(`<div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}><span>{"\ud83d\udd52"}</span><span>${escJsx(workingHoursText)}</span></div>`);

  if (contactItems.length > 0) {
    items.push(`<div style={{ fontSize: "0.9375rem" }}>\n    ${contactItems.join("\n    ")}\n  </div>`);
  }

  if (showMap && mapUrl) {
    items.push(`<iframe src="${esc(mapUrl)}" style={{ width: "100%", height: "300px", border: "none", borderRadius: "8px", marginTop: "16px" }} loading="lazy" allowFullScreen />`);
  }

  return `<div style={${cssObj(containerStyle)}}>\n  ${items.join("\n  ")}\n</div>`;
};

export const generateContactForm = (props: Props): string => {
  const {
    submitText = "Send Message",
    showPhone = true,
    showSubject = true,
    backgroundColor = "#ffffff",
    textColor = "#1f2937",
    padding = 32,
    borderRadius = 12,
    formAction = "",
  } = props;

  const inputStyle = `{{ width: "100%", padding: "${contactFormStyles.input.desktop.padding}", fontSize: "${contactFormStyles.input.desktop.fontSize}", border: "1px solid #d1d5db", borderRadius: "8px", boxSizing: "border-box" as const, fontFamily: "inherit" }}`;
  const labelStyle = `{{ display: "block", fontSize: "0.875rem", fontWeight: 500, marginBottom: "4px", color: "${textColor}" }}`;

  return `<form${formAction ? ` action="${esc(formAction)}" method="POST"` : ""} style={{ backgroundColor: "${backgroundColor}", padding: "${padding}px", borderRadius: "${borderRadius}px" }}>
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
    <div>
      <label style={${labelStyle}}>Name</label>
      <input type="text" name="name" placeholder="Your name" style={${inputStyle}} />
    </div>
    <div>
      <label style={${labelStyle}}>Email</label>
      <input type="email" name="email" placeholder="your@email.com" style={${inputStyle}} />
    </div>
  </div>
  ${showPhone ? `<div style={{ marginBottom: "16px" }}>\n    <label style={${labelStyle}}>Phone</label>\n    <input type="tel" name="phone" placeholder="Phone number" style={${inputStyle}} />\n  </div>` : ""}
  ${showSubject ? `<div style={{ marginBottom: "16px" }}>\n    <label style={${labelStyle}}>Subject</label>\n    <input type="text" name="subject" placeholder="Subject" style={${inputStyle}} />\n  </div>` : ""}
  <div style={{ marginBottom: "16px" }}>
    <label style={${labelStyle}}>Message</label>
    <textarea name="message" rows={5} placeholder="Your message" style={${inputStyle}} />
  </div>
  <button type="submit" style={{ padding: "${contactFormStyles.button.desktop.padding}", fontSize: "${contactFormStyles.button.desktop.fontSize}", background: "var(--color-primary, #3b82f6)", color: "#ffffff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, width: "100%" }}>
    ${escJsx(submitText)}
  </button>
</form>`;
};

export const generateTestimonial = (props: Props, assets: ImageAsset[] = [], childrenCode = ""): string => {
  const {
    quote = "Great service!",
    authorName = "",
    authorTitle = "",
    authorImage = "",
    rating = 5,
    backgroundColor = "#ffffff",
    textColor = "#1f2937",
    borderRadius = 12,
    textFontFamily,
  } = props;

  const stars = Array(5)
    .fill(0)
    .map((_, i) => (i < Number(rating) ? `<span style={{ color: "#fbbf24" }}>{"\u2605"}</span>` : `<span style={{ color: "#d1d5db" }}>{"\u2605"}</span>`))
    .join("");

  const ffStyle = (text?: unknown) => fontENStyle(text ?? "", textFontFamily);
  const textContent = childrenCode || `<p style={{ fontSize: "${testimonialStyles.quote.desktop.fontSize}", lineHeight: 1.7, marginBottom: "${testimonialStyles.quote.desktop.marginBottom}px", position: "relative", zIndex: 1${ffStyle(quote)} }}>${escJsx(quote)}</p>
  <div style={{ display: "flex", alignItems: "center", gap: "${testimonialStyles.author.desktop.gap}px" }}>
    ${authorImage ? `<img src=${imgRef(authorImage as string, assets)} alt="${esc(authorName)}" style={{ width: "${testimonialStyles.author.desktop.imageSize}px", height: "${testimonialStyles.author.desktop.imageSize}px", borderRadius: "50%", objectFit: "cover" }} />` : ""}
    <div>
      ${authorName ? `<div style={{ fontWeight: 600, fontSize: "${testimonialStyles.author.desktop.fontSize}"${ffStyle(authorName)} }}>${escJsx(authorName)}</div>` : ""}
      ${authorTitle ? `<div style={{ fontSize: "${testimonialStyles.author.desktop.titleFontSize}", opacity: 0.7${ffStyle(authorTitle)} }}>${escJsx(authorTitle)}</div>` : ""}
    </div>
  </div>`;

  return `<div style={{ backgroundColor: "${backgroundColor}", color: "${textColor}", borderRadius: "${borderRadius}px", padding: "${testimonialStyles.padding.desktop}px", boxShadow: "${shadows.card}", position: "relative" }}>
  <div style={{ position: "absolute", top: "${testimonialStyles.quoteMark.desktop.top}px", left: "${testimonialStyles.quoteMark.desktop.left}px", fontSize: "${testimonialStyles.quoteMark.desktop.fontSize}", color: "var(--color-primary, #3b82f6)", opacity: 0.2, lineHeight: 1 }}>{"\u201c"}</div>
  <div style={{ marginBottom: "16px" }}>${stars}</div>
  ${textContent}
</div>`;
};

export const generateServiceCard = (props: Props, assets: ImageAsset[], childrenCode = ""): string => {
  const {
    title = "Service",
    description = "",
    imageUrl = "",
    icon = "",
    link = "",
    backgroundColor = "#ffffff",
    textColor = "#1f2937",
    borderRadius = 12,
    padding = 24,
    showShadow = true,
    textFontFamily,
  } = props;

  const safePadding = padding || serviceCardStyles.padding.desktop;
  const imgHeight = serviceCardStyles.imageHeight.desktop;
  const titleFS = serviceCardStyles.title.desktop.fontSize;
  const descFS = serviceCardStyles.description.desktop.fontSize;

  // Card styles matching editor exactly: ServiceCard.tsx line 130-144
  // Parent carousel overrides flex via --card-width CSS variable
  const cardStyle: Record<string, unknown> = {
    flex: "0 0 var(--card-width, 280px)",
    minWidth: 0,
    backgroundColor: backgroundColor as string,
    color: textColor as string,
    borderRadius: `${borderRadius}px`,
    padding: imageUrl ? 0 : `${safePadding}px`,
    paddingBottom: `${safePadding}px`,
    boxShadow: showShadow ? shadows.card : "none",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  // Inner padding for text content when image exists
  const innerPad = imageUrl ? `<div style={{ padding: "0 ${safePadding}px" }}>` : "";
  const innerPadClose = imageUrl ? `</div>` : "";

  let content = "";

  // Image/icon part — imageUrl takes priority over icon (matching ServiceCardExport)
  if (imageUrl) {
    content = `<div style={{ marginBottom: "16px" }}>\n  <img src=${imgRef(imageUrl as string, assets)} alt="${esc(title)}" style={{ width: "100%", height: "${imgHeight}px", objectFit: "cover", borderRadius: "${borderRadius}px ${borderRadius}px 0 0" }} />\n</div>\n${innerPad}`;
  } else if (icon) {
    content = `<div style={{ fontSize: "2.25rem", marginBottom: "16px" }}>${escJsx(icon)}</div>\n${innerPad}`;
  } else {
    content = `${innerPad}`;
  }

  // Text content part — use childrenCode if provided
  const ffStyle = (text?: unknown) => fontENStyle(text ?? "", textFontFamily);
  const textContent = childrenCode || `<h4 style={{ fontSize: "${titleFS}", fontWeight: 600, marginBottom: "8px", wordWrap: "break-word", overflowWrap: "break-word"${ffStyle(title)} }}>${escJsx(title)}</h4>\n${description ? `<p style={{ fontSize: "${descFS}", opacity: 0.8, lineHeight: 1.6, wordWrap: "break-word", overflowWrap: "break-word"${ffStyle(description)} }}>${escJsx(description)}</p>\n` : ""}`;
  content += textContent;
  content += innerPadClose;

  if (link) {
    return `<a href="${esc(link)}" style={{ textDecoration: "none", color: "inherit" }}><div style={${cssObj(cardStyle)}}>\n  ${content}</div></a>`;
  }
  return `<div style={${cssObj(cardStyle)}}>\n  ${content}</div>`;
};

export const generateDoctorCard = (props: Props, assets: ImageAsset[], childrenCode = ""): string => {
  const {
    name = "Doctor",
    specialty = "",
    imageUrl = "",
    description = "",
    backgroundColor = "#ffffff",
    textColor = "#1f2937",
    borderRadius = 12,
    imageSize = "medium",
    textFontFamily,
  } = props;

  const imageSizes: Record<string, number> = { small: 80, medium: 120, large: 160 };
  const size = imageSizes[imageSize as string] || 120;

  const imageContent = imageUrl ? `<img src=${imgRef(imageUrl as string, assets)} alt="${esc(name)}" style={{ width: "${size}px", height: "${size}px", borderRadius: "50%", objectFit: "cover", margin: "0 auto 16px" }} />` : `<div style={{ width: "${size}px", height: "${size}px", borderRadius: "50%", backgroundColor: "#e5e7eb", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem" }}>{"\ud83d\udc68\u200d\u2695\ufe0f"}</div>`;

  const ffStyle = (text?: unknown) => fontENStyle(text ?? "", textFontFamily);
  const textContent = childrenCode || `<h4 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "4px"${ffStyle(name)} }}>${escJsx(name)}</h4>
  ${specialty ? `<p style={{ fontSize: "0.875rem", color: "var(--color-primary, #3b82f6)", marginBottom: "8px"${ffStyle(specialty)} }}>${escJsx(specialty)}</p>` : ""}
  ${description ? `<p style={{ fontSize: "0.8125rem", color: "#6b7280", lineHeight: 1.5${ffStyle(description)} }}>${escJsx(description)}</p>` : ""}`;

  return `<div style={{ flex: "0 0 var(--card-width, 280px)", minWidth: 0, backgroundColor: "${backgroundColor}", color: "${textColor}", borderRadius: "${borderRadius}px", padding: "24px", boxShadow: "${shadows.card}", textAlign: "center" }}>
  ${imageContent}
  ${textContent}
</div>`;
};

export const generateFeatureCard = (props: Props, assets: ImageAsset[], childrenCode = ""): string => {
  const {
    title = "Feature",
    description = "",
    imageUrl = "",
    imageHeight = 60,
    category = "",
    categoryColor = "#E8834A",
    link = "",
    linkText = "Learn more",
    backgroundColor = "#ffffff",
    textColor = "#2C2C2B",
    borderRadius = 12,
    showShadow = false,
    textFontFamily,
  } = props;

  const cardStyle: Record<string, unknown> = {
    position: "relative",
    backgroundColor: backgroundColor as string,
    color: textColor as string,
    borderRadius: `${borderRadius}px`,
    padding: "24px",
    minHeight: "195px",
    overflow: "hidden",
    boxShadow: showShadow ? shadows.card : "none",
  };

  const ffStyle = (text?: unknown) => fontENStyle(text ?? "", textFontFamily);
  const textContent = childrenCode || `<h4 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "8px", paddingRight: "80px"${ffStyle(title)} }}>${escJsx(title)}</h4>
  ${description ? `<p style={{ fontSize: "1rem", color: "#6b7280", lineHeight: 1.5, paddingRight: "80px"${ffStyle(description)} }}>${escJsx(description)}</p>` : ""}`;

  return `<div className="feature-card" style={${cssObj(cardStyle)}}>
  ${textContent}
  ${imageUrl ? `<img src=${imgRef(imageUrl as string, assets)} alt="${esc(title)}" style={{ position: "absolute", bottom: "0", right: "8px", width: "${(imageHeight || 60) + 36}px", height: "auto", maxHeight: "${(imageHeight || 60) + 36}px", objectFit: "contain" }} />` : ""}
  ${category ? `<span style={{ position: "absolute", bottom: "16px", left: "16px", padding: "4px 12px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 600, color: "${categoryColor}", background: "linear-gradient(90deg, ${categoryColor}22 0%, ${categoryColor}44 100%)" }}>${escJsx(category)}</span>` : ""}
  ${link ? `<a href="${esc(link)}" style={{ display: "inline-block", marginTop: "12px", fontSize: "0.875rem", color: "var(--color-primary, #3b82f6)", textDecoration: "none", fontWeight: 500 }}>${escJsx(linkText)} &gt;</a>` : ""}
</div>`;
};

export const generatePricingCard = (props: Props, childrenCode = ""): string => {
  const {
    planName = "\u0E41\u0E1E\u0E47\u0E01\u0E40\u0E25\u0E47\u0E01\u0E2A\u0E38\u0E14\u0E04\u0E38\u0E49\u0E21",
    credit = "600",
    price = "300",
    unit = "\u0E3F/\u0E04\u0E23\u0E31\u0E49\u0E07",
    services = [],
    note = "",
    badgeColor = "linear-gradient(180deg, #F2819F 0%, #FAAF40 100%)",
    headerBackground = "linear-gradient(116.39deg, #FFEBCF 2.25%, #EFFFF3 55.82%, #FFF7EE 100%)",
    bahtHeaderBg = "linear-gradient(90deg, #FFECCF 0%, #DAFFDC 100%)",
    checkColor = "#00B97E",
    headerTextColor = "#2C2C2B",
    backgroundColor = "#ffffff",
    textColor = "#2C2C2B",
    borderRadius = 20,
    showShadow = true,
    textFontFamily,
  } = props;

  const safeServices = safeArray(services);

  // Split unit: "฿/ครั้ง" → currency "฿" + suffix "/ครั้ง"
  const unitStr = String(unit);
  const slashIdx = unitStr.indexOf("/");
  const currencyPart = slashIdx > 0 ? ` ${unitStr.substring(0, slashIdx)}` : "";
  const unitSuffix = slashIdx > 0 ? unitStr.substring(slashIdx) : unitStr;

  const checkSvg = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${checkColor}" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>`;

  const ffVal = textFontFamily ? `'${esc(textFontFamily)}', var(--font-family), sans-serif` : "var(--font-family)";

  const serviceRows = safeServices
    .map((s) => {
      const svc = s as Record<string, unknown>;
      const hasValues = !!(svc.credit || svc.baht);
      const isLongText = !svc.credit && svc.baht && String(svc.baht).length > 6;

      let valuesCols = "";
      if (isLongText) {
        valuesCols = `<span style={{ width: "126px", fontFamily: "${ffVal}", fontSize: "0.6875rem", color: "#000000", textAlign: "right", lineHeight: 1.3, flexShrink: 0 }}>${escJsx(svc.baht || "")}</span>`;
      } else if (hasValues) {
        valuesCols = `<span style={{ width: "55px", fontFamily: "${ffVal}", fontSize: "0.875rem", fontWeight: 400, color: "#000000", textAlign: "center", flexShrink: 0 }}>${escJsx(svc.credit || "")}</span>
        <span style={{ width: "6px", flexShrink: 0 }} />
        <span style={{ width: "65px", fontFamily: "${ffVal}", fontSize: "0.875rem", fontWeight: 400, color: "#000000", textAlign: "center", flexShrink: 0 }}>${escJsx(svc.baht || "")}</span>`;
      } else {
        valuesCols = `<span style={{ width: "55px", display: "flex", justifyContent: "center", flexShrink: 0 }}>${checkSvg}</span>
        <span style={{ width: "6px", flexShrink: 0 }} />
        <span style={{ width: "65px", display: "flex", justifyContent: "center", flexShrink: 0 }}>${checkSvg}</span>`;
      }

      const descText = `${escJsx(svc.description || "")}${svc.description && svc.note ? " " : ""}${escJsx(svc.note || "")}`;
      const descLine = (svc.description || svc.note)
        ? `\n        <span style={{ fontFamily: "${ffVal}", fontSize: "0.625rem", color: "#000000", opacity: 0.6, lineHeight: 1.2, display: "block", whiteSpace: "pre-line" }}>${descText.replace(/\n/g, '{"\\n"}')}</span>`
        : "";

      return `<div style={{ display: "flex", alignItems: "center", padding: "3px 16px", minHeight: "26px" }}>
      <div style={{ flex: 1, minWidth: 0, paddingLeft: "4px" }}>
        <span style={{ fontFamily: "${ffVal}", fontSize: "0.8125rem", fontWeight: 400, color: "#000000", lineHeight: 1.4, display: "block" }}>${escJsx(svc.name || "")}</span>${descLine}
      </div>
      ${valuesCols}
    </div>`;
    })
    .join("\n    ");

  const serviceTable = safeServices.length > 0 ? `
  <div style={{ paddingBottom: "16px" }}>
    {/* Table Header */}
    <div style={{ display: "flex", margin: "0 16px", gap: "6px", marginBottom: "4px" }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", backgroundColor: "#404040", borderRadius: "30px", height: "24px", overflow: "hidden" }}>
        <span style={{ flex: 1, paddingLeft: "12px", fontFamily: "${ffVal}", fontSize: "0.75rem", fontWeight: 400, color: "#ffffff" }}>Service</span>
        <span style={{ width: "55px", textAlign: "center", fontFamily: "${ffVal}", fontSize: "0.75rem", fontWeight: 400, color: "#ffffff" }}>Credit</span>
      </div>
      <div style={{ width: "65px", display: "flex", alignItems: "center", justifyContent: "center", background: "${esc(String(bahtHeaderBg))}", borderRadius: "30px", height: "24px", flexShrink: 0 }}>
        <span style={{ fontFamily: "${ffVal}", fontSize: "0.75rem", fontWeight: 400, color: "${textColor}" }}>Baht</span>
      </div>
    </div>
    {/* Table Rows */}
    ${serviceRows}
  </div>` : "";

  const headerContent = childrenCode || `{/* Header gradient area (inset) */}
  <div style={{ margin: "14px 14px 0", background: "${esc(String(headerBackground))}", borderRadius: "${borderRadius}px", padding: "10px 16px 14px", display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: "90px" }}>
    {/* Plan name badge — white pill */}
    <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", backgroundColor: "rgba(255, 255, 255, 0.8)", borderRadius: "20px", padding: "2px 10px", alignSelf: "flex-start" }}>
      <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "${esc(String(badgeColor))}", flexShrink: 0, display: "inline-block" }} />
      <span style={{ fontFamily: "${ffVal}", fontSize: "0.875rem", fontWeight: 300, color: "#000000", lineHeight: 1.4 }}>${escJsx(planName)}</span>
    </div>
    {/* Credits — bottom right */}
    <div style={{ alignSelf: "flex-end", display: "flex", alignItems: "baseline", gap: "4px", marginTop: "4px" }}>
      <span style={{ fontFamily: "'Fahkwang', var(--font-family), sans-serif", fontSize: "2rem", fontWeight: 400, color: "${headerTextColor}", lineHeight: 1 }}>${escJsx(credit)}</span>
      <span style={{ fontFamily: "'Fahkwang', var(--font-family), sans-serif", fontSize: "1rem", fontWeight: 400, color: "${headerTextColor}", lineHeight: 1 }}>Credits</span>
    </div>
  </div>
  {/* Price section */}
  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "14px 30px 10px" }}>
    <span style={{ fontFamily: "${ffVal}", fontSize: "1.125rem", fontWeight: 400, color: "${textColor}" }}>{"\u0E23\u0E32\u0E04\u0E32"}</span>
    <div style={{ display: "flex", alignItems: "baseline" }}>
      <span style={{ fontFamily: "${ffVal}", fontSize: "2.25rem", fontWeight: 500, color: "${textColor}", lineHeight: 1.2 }}>${escJsx(price)}${currencyPart}</span>
      <span style={{ fontFamily: "${ffVal}", fontSize: "1.125rem", fontWeight: 400, color: "${textColor}", marginLeft: "4px" }}>${escJsx(unitSuffix)}</span>
    </div>
  </div>`;

  return `<div style={{ position: "relative", borderRadius: "${borderRadius}px", overflow: "hidden", backgroundColor: "${backgroundColor}", boxShadow: ${showShadow ? '"0px 0px 20px 5px rgba(0, 0, 0, 0.1)"' : '"none"'}, width: "100%", maxWidth: "320px" }}>
  ${headerContent}${serviceTable}
  ${note ? `<div style={{ padding: "0 16px 12px" }}><p style={{ fontFamily: "var(--font-family)", fontSize: "0.6875rem", color: "${textColor}", opacity: 0.5, textAlign: "left", margin: 0, wordWrap: "break-word" }}>${escJsx(note)}</p></div>` : ""}
</div>`;
};

export const generatePromotionBanner = (props: Props, assets: ImageAsset[], childrenCode = ""): string => {
  const {
    title = "Special Offer",
    description = "",
    discount = "20% OFF",
    backgroundImage = "",
    backgroundColor = "#7c3aed",
    textColor = "#ffffff",
    accentColor = "#fbbf24",
    buttonText = "",
    buttonLink = "#",
    overlayOpacity = 0.5,
    textAlign = "center",
    borderRadius = 0,
    expiryText = "",
    textFontFamily,
  } = props;

  const accentStr = String(accentColor);
  const isGradientAccent = accentStr.includes("gradient");
  const accentTextStyle = isGradientAccent
    ? `background: "${esc(accentStr)}", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"`
    : `color: "${esc(accentStr)}"`;

  // Handle background image with import reference
  let bgOverlay = "";
  if (backgroundImage) {
    const bgRef = getImageReference(backgroundImage as string, assets);
    if (bgRef.type === "import") {
      bgOverlay = `<div style={{ position: "absolute", inset: 0, backgroundImage: \`url(\${${bgRef.value}})\`, backgroundSize: "cover", backgroundPosition: "center", opacity: ${1 - Number(overlayOpacity)} }} />`;
    } else {
      bgOverlay = `<div style={{ position: "absolute", inset: 0, backgroundImage: "url(${esc(backgroundImage)})", backgroundSize: "cover", backgroundPosition: "center", opacity: ${1 - Number(overlayOpacity)} }} />`;
    }
  }

  const ffStyle = (text?: unknown) => fontENStyle(text ?? "", textFontFamily);
  const textContent = childrenCode || `${discount ? `<div style={{ fontSize: "3rem", fontWeight: 800, ${accentTextStyle}, marginBottom: "12px" }}>${escJsx(discount)}</div>` : ""}
    ${title ? `<h3 style={{ fontSize: "1.75rem", fontWeight: 700, marginBottom: "12px"${ffStyle(title)} }}>${escJsx(title)}</h3>` : ""}
    ${description ? `<p style={{ fontSize: "1rem", opacity: 0.9, marginBottom: "24px"${ffStyle(description)} }}>${escJsx(description)}</p>` : ""}
    ${expiryText ? `<p style={{ fontSize: "0.875rem", opacity: 0.8, marginBottom: "16px" }}>{"\ud83d\udd52"} ${escJsx(expiryText)}</p>` : ""}
    ${buttonText ? `<a href="${esc(buttonLink)}" style={{ display: "inline-block", padding: "12px 32px", background: "${esc(accentStr)}", color: "#1f2937", borderRadius: "8px", fontWeight: 600, textDecoration: "none" }}>${escJsx(buttonText)}</a>` : ""}`;

  return `<div style={{ position: "relative", backgroundColor: "${backgroundColor}", color: "${textColor}", padding: "48px 32px", borderRadius: "${borderRadius}px", overflow: "hidden", textAlign: "${textAlign}" }}>
  ${bgOverlay}
  <div style={{ position: "relative", zIndex: 1 }}>
    ${textContent}
  </div>
</div>`;
};

export const generateFloatingCTA = (props: Props): string => {
  const {
    buttons = [],
    position = "bottom-right",
    buttonSize = 56,
    gap = 12,
  } = props;

  const safeButtons = safeArray(buttons);
  if (safeButtons.length === 0) return `{/* No floating CTA buttons */}`;

  const isRight = (position as string).includes("right");

  const containerStyle: Record<string, unknown> = {
    position: "fixed",
    bottom: "24px",
    [isRight ? "right" : "left"]: "24px",
    display: "flex",
    flexDirection: "column",
    gap: `${gap}px`,
    zIndex: 9999,
  };

  const btnCode = safeButtons
    .map((b) => {
      const btn = b as Record<string, unknown>;
      const type = (btn.type || "phone") as string;
      const url = (btn.url || btn.link || "#") as string;
      const label = (btn.label || type) as string;

      const colorMap: Record<string, string> = {
        line: "#06C755",
        whatsapp: "#25D366",
        phone: "var(--color-primary, #3b82f6)",
        facebook: "#1877F2",
      };

      const btnStyle: Record<string, unknown> = {
        width: `${buttonSize}px`,
        height: `${buttonSize}px`,
        borderRadius: "50%",
        backgroundColor: colorMap[type] || "var(--color-primary, #3b82f6)",
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textDecoration: "none",
        boxShadow: shadows.lg,
        fontSize: `${Math.round(Number(buttonSize) * 0.4)}px`,
        fontWeight: 700,
        border: "none",
        cursor: "pointer",
      };

      const iconMap: Record<string, string> = {
        line: "L",
        whatsapp: "W",
        phone: "\ud83d\udcde",
        facebook: "f",
      };

      return `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer" style={${cssObj(btnStyle)}} title="${esc(label)}">${iconMap[type] || type.charAt(0).toUpperCase()}</a>`;
    })
    .join("\n    ");

  return `<div style={${cssObj(containerStyle)}}>\n    ${btnCode}\n  </div>`;
};

export const generateLineLoginButton = (props: Props): string => {
  const {
    buttonText = "\u0e40\u0e02\u0e49\u0e32\u0e2a\u0e39\u0e48\u0e23\u0e30\u0e1a\u0e1a\u0e14\u0e49\u0e27\u0e22 LINE",
    style = "green",
    size = "medium",
    borderRadius = 8,
    liffId = "",
    apiHost = "",
    appName = "",
    redirectUrl = "",
  } = props;

  const sizeMap: Record<string, { px: number; py: number; fontSize: string }> = {
    small: { px: 16, py: 8, fontSize: "0.875rem" },
    medium: { px: 24, py: 12, fontSize: "1rem" },
    large: { px: 32, py: 16, fontSize: "1.125rem" },
  };
  const s = sizeMap[size as string] || sizeMap.medium;

  const bgColor = style === "green" ? "#06C755" : "#ffffff";
  const txtColor = style === "green" ? "#ffffff" : "#06C755";
  const border = style === "green" ? "none" : "2px solid #06C755";

  return `<button
  style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "${s.py}px ${s.px}px", backgroundColor: "${bgColor}", color: "${txtColor}", border: "${border}", borderRadius: "${borderRadius}px", fontSize: "${s.fontSize}", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
  data-liff-id="${esc(liffId)}"
  data-api-host="${esc(apiHost)}"
  data-app-name="${esc(appName)}"
  data-redirect-url="${esc(redirectUrl)}"
>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/></svg>
  ${escJsx(buttonText)}
</button>`;
};

// ─────────────────────────────────────────────
// Container (supports recursive children)
// ─────────────────────────────────────────────

export const generateContainer = (props: Props, childrenCode: string, assets: ImageAsset[] = []): string => {
  const {
    padding = 16,
    paddingX,
    paddingY,
    paddingLeft,
    paddingRight,
    gap = 0,
    flexDirection = "column",
    justifyContent = "start",
    alignItems = "stretch",
    background = "transparent",
    maxWidth = "full",
    minHeight,
    borderRadius = 0,
    boxShadow = "none",
    width = "100%",
    backgroundImage,
    backgroundSize = "cover",
    backgroundPosition = "center",
    overflow = "visible",
    hideOnMobile = false,
    hideOnDesktop = false,
    order,
    sectionId,
    margin = 0,
    marginTop,
  } = props;

  const jcMap = justifyContentMap as Record<string, string>;
  const aiMap = alignItemsMap as Record<string, string>;
  const mwMap = containerMaxWidths as Record<string, string>;

  const style: Record<string, unknown> = {
    display: "flex",
    flexDirection: flexDirection as string,
    justifyContent: jcMap[justifyContent as string] || (justifyContent as string) || "flex-start",
    alignItems: aiMap[alignItems as string] || (alignItems as string) || "stretch",
    gap: gap ? `${gap}px` : undefined,
    padding: (paddingLeft != null || paddingRight != null || paddingX != null || paddingY != null)
      ? `${paddingY ?? padding}px ${paddingRight ?? paddingX ?? padding}px ${paddingY ?? padding}px ${paddingLeft ?? paddingX ?? padding}px`
      : `${padding}px`,
    background: background as string,
    maxWidth: mwMap[maxWidth as string] || "100%",
    width: typeof width === "number" ? `${width}px` : width,
    minHeight: minHeight ? `${minHeight}px` : undefined,
    borderRadius: borderRadius != null ? `${borderRadius}px` : undefined,
    boxShadow: (shadows as Record<string, string>)[boxShadow as string] || "none",
    overflow: overflow as string,
    marginTop: marginTop != null ? `${marginTop}px` : (margin ? `${margin}px` : undefined),
    marginBottom: margin ? `${margin}px` : undefined,
    marginLeft: (maxWidth !== "full" || (width && width !== "100%" && width !== "auto")) ? "auto" : (margin ? `${margin}px` : undefined),
    marginRight: (maxWidth !== "full" || (width && width !== "100%" && width !== "auto")) ? "auto" : (margin ? `${margin}px` : undefined),
    boxSizing: "border-box" as const,
  };

  let bgImageExpr = "";
  if (backgroundImage) {
    const ref = getImageReference(backgroundImage as string, assets);
    if (ref.type === "import") {
      bgImageExpr = ref.value;
    } else {
      style.backgroundImage = `url(${ref.value})`;
    }
    style.backgroundSize = backgroundSize;
    style.backgroundPosition = backgroundPosition;
  }
  if (order !== undefined) style.order = order;

  const dataAttrs: string[] = [];
  if (hideOnMobile) dataAttrs.push(`data-hide-on-mobile="true"`);
  if (hideOnDesktop) dataAttrs.push(`data-hide-on-desktop="true"`);
  if (sectionId) dataAttrs.push(`id="${esc(sectionId)}"`);

  // Add responsive classNames
  const disableResp = props.disableResponsive;
  const classNames: string[] = [];
  if (flexDirection === "row" && !disableResp && width !== "auto" && alignItems !== "baseline") classNames.push("mobile-stack");
  if (width && width !== "100%" && width !== "auto") classNames.push("mobile-full-width");

  // Per-element mobile padding (driven by mobilePaddingX/Y props, not hardcoded selectors)
  const mpX = props.mobilePaddingX as number | undefined;
  const mpY = props.mobilePaddingY as number | undefined;
  const hasMobilePad = mpX != null || mpY != null;
  if (hasMobilePad) classNames.push("mobile-pad");

  const classAttr = classNames.length > 0 ? `className="${classNames.join(" ")}"` : "";

  let styleStr = cssObj(style);
  if (bgImageExpr) {
    styleStr = styleStr.replace(/\s*\}$/, `, backgroundImage: \`url(\${${bgImageExpr}})\` }`);
  }
  if (hasMobilePad) {
    const mpx = mpX != null ? mpX : (paddingX ?? padding);
    const mpy = mpY != null ? mpY : (paddingY ?? padding);
    styleStr = styleStr.replace(/\s*\}$/, `, "--mobile-pad": "${mpy}px ${mpx}px" }`);
  }

  return `<div style={${styleStr}} ${dataAttrs.join(" ")} ${classAttr}>
  ${childrenCode}
</div>`;
};

// ─────────────────────────────────────────────
// Carousel containers (Services, Doctors) — standalone section with useRef + onClick
// ─────────────────────────────────────────────

/**
 * Generate a complete standalone TSX section file for carousel (Services/Doctors).
 * Uses useRef + onClick for arrow navigation — matching ContainerSection3.tsx pattern.
 */
export const generateCarouselSection = (
  type: "services" | "doctors",
  props: Props,
  childrenCode: string,
  sectionName: string,
): string => {
  const {
    gap = 24,
    showNavigation = true,
    cardsPerViewDesktop = 3,
  } = props;

  const cpv = Number(cardsPerViewDesktop) || 3;
  const gapNum = Number(gap) || 24;
  const cardWidthCalc = `calc((100% - ${(cpv - 1) * gapNum}px) / ${cpv})`;
  const emptyMsg = type === "services" ? "No services added yet" : "No doctors added yet";

  const content = childrenCode || `<div style={{ padding: "40px", textAlign: "center", color: "#9ca3af", width: "100%" }}>${emptyMsg}</div>`;

  const navCode = showNavigation
    ? `
        <button onClick={() => scroll(-1)} disabled={atStart} style={{ ...arrowStyle, left: "4px", opacity: atStart ? 0.3 : 1, cursor: atStart ? "default" : "pointer" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><polyline points="15,18 9,12 15,6" /></svg>
        </button>`
    : "";

  const navCodeRight = showNavigation
    ? `
        <button onClick={() => scroll(1)} disabled={atEnd} style={{ ...arrowStyle, right: "4px", opacity: atEnd ? 0.3 : 1, cursor: atEnd ? "default" : "pointer" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><polyline points="9,18 15,12 9,6" /></svg>
        </button>`
    : "";

  return `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect } from "react";

const ${sectionName}: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 0);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, []);

  const scroll = (dir: number) => {
    if (trackRef.current) {
      trackRef.current.scrollBy({ left: dir * trackRef.current.clientWidth, behavior: "smooth" });
    }
  };

  const arrowStyle = { position: "absolute" as const, top: "50%", transform: "translateY(-50%)", zIndex: 10, width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#ffffff", border: "none", boxShadow: "0 1px 5px rgba(0,0,0,0.15)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "opacity 0.2s" };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div style={{ position: "relative" }}>${navCode}
        <div ref={trackRef} style={{ "--card-width": "${cardWidthCalc}", display: "flex", flexWrap: "nowrap", gap: "${gapNum}px", overflowX: "auto", scrollBehavior: "smooth", padding: "16px", scrollbarWidth: "none" } as React.CSSProperties}>
          ${content}
        </div>${navCodeRight}
      </div>
    </div>
  );
};

export default ${sectionName};
`;
};

// ─────────────────────────────────────────────
// Interactive Components (need hooks - standalone files)
// ─────────────────────────────────────────────

/**
 * Generate a complete standalone TSX file for FAQ accordion
 */
export const generateFAQSection = (props: Props, sectionName: string): string => {
  const {
    items = [],
    backgroundColor = "#ffffff",
    textColor = "#1f2937",
    accentColor = "var(--color-primary, #3b82f6)",
    borderRadius = 8,
    padding = 16,
    allowMultiple = false,
    questionFontSize,
    answerFontSize,
  } = props;

  const safeItems = safeArray(items);
  const itemsJson = JSON.stringify(
    safeItems.map((i) => {
      const item = i as Record<string, unknown>;
      return { question: item.question || "", answer: item.answer || "" };
    }),
    null,
    2,
  );

  const qFontSize = questionFontSize || faqStyles.question.desktop.fontSize;
  const aFontSize = answerFontSize || faqStyles.answer.desktop.fontSize;

  return `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState } from "react";

const ${sectionName}: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  const toggle = (index: number) => {
    setOpenItems((prev) => {
      const next = new Set(${allowMultiple ? "prev" : ""});
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const items = ${itemsJson};

  return (
    <div style={{ backgroundColor: "${backgroundColor}", color: "${textColor}", borderRadius: "${borderRadius}px", padding: "${padding}px" }}>
      {items.map((item, index) => (
        <div key={index} style={{ borderBottom: "1px solid #e5e7eb" }}>
          <button
            onClick={() => toggle(index)}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "${faqStyles.question.desktop.padding}",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "${qFontSize}",
              fontWeight: 500,
              color: "inherit",
              textAlign: "left",
              fontFamily: "inherit",
            }}
          >
            <span>{item.question}</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ transition: "transform 0.2s", transform: openItems.has(index) ? "rotate(180deg)" : "rotate(0deg)" }}><path d="M5 7.5L10 12.5L15 7.5" stroke="${accentColor}" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <div
            style={{
              maxHeight: openItems.has(index) ? "384px" : "0px",
              overflow: "hidden",
              transition: "max-height 0.3s ease",
            }}
          >
            <p style={{ padding: "0 0 16px 0", fontSize: "${aFontSize}", lineHeight: 1.7, color: "#6b7280" }}>
              {item.answer}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ${sectionName};
`;
};

/**
 * Generate a complete standalone TSX file for Navbar with mobile menu
 */
export const generateNavbarSection = (props: Props, sectionName: string, assets: ImageAsset[]): string => {
  const {
    logoUrl = "",
    logoText = "",
    navItems = [],
    extraButtons = [],
    extraIcons = [],
    ctaText: rawCtaText = "",
    ctaLink = "#",
    ctaBackgroundColor = "",
    ctaHideOnMobile = false,
    showLangSelector = false,
    languages = [],
    backgroundColor = "#ffffff",
    textColor = "#1f2937",
    isStickyNav = true,
    paddingX = 24,
    paddingY = 12,
    maxWidth = "1280px",
    navFontSize = 14,
    navFontFamily,
  } = props;

  const ctaText = resolveText(rawCtaText);
  const navFfStyle = navFontFamily ? `fontFamily: "'${esc(navFontFamily)}', var(--font-family), sans-serif",` : "";
  const safeNavItems = safeArray(navItems);
  const safeExtraButtons = safeArray(extraButtons);
  const safeExtraIcons = safeArray(extraIcons);

  // Check if any nav item has multilingual label
  const hasMultilingualNav = safeNavItems.some((n) => typeof (n as Record<string, unknown>).label === "object");

  const navItemsJson = JSON.stringify(
    safeNavItems.map((n) => {
      const item = n as Record<string, unknown>;
      const label = item.label;
      if (typeof label === "object" && label !== null) {
        return { label: label as Record<string, string>, href: item.href || "#" };
      }
      return { label: String(label || ""), href: item.href || "#" };
    }),
  );

  // CTA background color
  const ctaBg = ctaBackgroundColor || "#2C2C2B";

  // Image imports for logo + extra icons
  const imports: string[] = [];
  if (logoUrl && typeof logoUrl === "string") {
    const ref = getImageReference(logoUrl as string, assets);
    if (ref.type === "import") {
      imports.push(`import ${ref.value} from "../assets/${assets.find((a) => a.importName === ref.value)?.filename}";`);
    }
  }
  safeExtraIcons.forEach((icon) => {
    const iconObj = icon as Record<string, unknown>;
    const iconUrl = iconObj.iconUrl as string;
    if (iconUrl) {
      const ref = getImageReference(iconUrl, assets);
      if (ref.type === "import") {
        const asset = assets.find((a) => a.importName === ref.value);
        if (asset) {
          imports.push(`import ${ref.value} from "../assets/${asset.filename}";`);
        }
      }
    }
  });

  const logoSrc = logoUrl ? imgRef(logoUrl as string, assets) : `"#"`;
  const hasLogo = !!(logoUrl || logoText);

  // Generate extra icon elements
  const extraIconElements = safeExtraIcons.map((icon) => {
    const iconObj = icon as Record<string, unknown>;
    const iconUrl = iconObj.iconUrl as string;
    const link = (iconObj.link as string) || "#";
    const bgColor = iconObj.bgColor as string;
    if (!iconUrl) return "";
    const src = imgRef(iconUrl, assets);
    const bgStyle = bgColor ? ` backgroundColor: "${bgColor}", borderRadius: "50%", padding: "4px",` : "";
    if (link.startsWith("#")) {
      return `<button onClick={() => onNavigate?.("${esc(link)}")} style={{ display: "flex", alignItems: "center",${bgStyle} background: "none", border: "none", padding: 0, cursor: "pointer" }}><img src=${src} alt="" style={{ height: "24px", width: "24px", objectFit: "contain" }} /></button>`;
    }
    const isExternalIcon = link.startsWith("http://") || link.startsWith("https://");
    return isExternalIcon
      ? `<a href="${esc(link)}" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center",${bgStyle} textDecoration: "none" }}><img src=${src} alt="" style={{ height: "24px", width: "24px", objectFit: "contain" }} /></a>`
      : `<a href="${esc(link)}" onClick={(e) => handleClick(e, "${esc(link)}")} style={{ display: "flex", alignItems: "center",${bgStyle} textDecoration: "none" }}><img src=${src} alt="" style={{ height: "24px", width: "24px", objectFit: "contain" }} /></a>`;
  }).filter(Boolean);

  // Generate extra button elements (desktop only)
  const extraButtonElements = safeExtraButtons
    .filter((b) => !(b as Record<string, unknown>).hideOnDesktop)
    .map((b) => {
      const btn = b as Record<string, unknown>;
      const variant = (btn.variant as string) || "filled";
      const bgColor = (btn.bgColor as string) || "#2C2C2B";
      const text = resolveText(btn.text);
      const link = (btn.link as string) || "#";
      let btnStyle: string;
      if (variant === "outline") {
        btnStyle = `padding: "8px 16px", background: "transparent", color: "${textColor}", border: "1px solid ${textColor}", borderRadius: "8px"`;
      } else if (variant === "text") {
        btnStyle = `padding: "8px 16px", background: "none", color: "${textColor}", border: "none"`;
      } else {
        btnStyle = `padding: "8px 16px", background: "${esc(bgColor)}", color: "#ffffff", border: "none", borderRadius: "8px"`;
      }
      const btnFontEN = !/[\u0E00-\u0E7F]/.test(text) ? `, fontFamily: "var(--font-family-en, var(--font-family))"` : "";
      return `<a href="${esc(link)}" onClick={(e) => handleClick(e, "${esc(link)}")} style={{ ${btnStyle}, textDecoration: "none", fontWeight: 600, fontSize: "0.875rem", whiteSpace: "nowrap"${btnFontEN} }}>${escJsx(text)}</a>`;
    });

  // Generate extra button elements (mobile only) — match editor NavbarMobileSidebar
  const mobileExtraButtonElements = safeExtraButtons
    .filter((b) => !(b as Record<string, unknown>).hideOnMobile)
    .map((b) => {
      const btn = b as Record<string, unknown>;
      const bgColor = (btn.bgColor as string) || textColor;
      const text = resolveText(btn.text);
      const link = (btn.link as string) || "#";
      const variant = (btn.variant as string) || "filled";
      let btnStyle: string;
      if (variant === "text") {
        btnStyle = `display: "block", paddingTop: "8px", paddingBottom: "8px", fontWeight: 500, color: "${textColor}", textAlign: "left", borderBottom: "1px solid #e5e7eb"`;
      } else if (variant === "outline") {
        btnStyle = `display: "block", padding: "8px 24px", color: "${textColor}", border: "1.5px solid ${textColor}", backgroundColor: "transparent", borderRadius: "8px", fontWeight: 600, textAlign: "center"`;
      } else {
        btnStyle = `display: "block", padding: "8px 24px", backgroundColor: "${esc(bgColor)}", color: "#ffffff", border: "none", borderRadius: "8px", fontWeight: 600, textAlign: "center"`;
      }
      const mBtnFontEN = !/[\u0E00-\u0E7F]/.test(text) ? `, fontFamily: "var(--font-family-en, var(--font-family))"` : "";
      return `<a href="${esc(link)}" onClick={(e) => { setMobileOpen(false); handleClick(e, "${esc(link)}"); }} style={{ ${btnStyle}, textDecoration: "none"${mBtnFontEN} }}>${escJsx(text)}</a>`;
    });

  // Language selector (dropdown)
  const safeLanguages = safeArray(languages);
  const langJsonStr = JSON.stringify(safeLanguages.map((l) => String(l)));
  const hasLangSelector = showLangSelector && safeLanguages.length > 0;

  // Build right-side items for desktop nav
  const rightItems: string[] = [
    ...(hasLangSelector ? [`<div style={{ position: "relative", display: "inline-block" }} ref={langRef}>
            <button onClick={() => setLangOpen(!langOpen)} style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", padding: "4px 8px", borderRadius: "4px", fontSize: "0.85rem", fontWeight: 500, color: "inherit", background: "none", border: "none", fontFamily: "inherit" }}>
              <img src={selectedLang === "TH" ? flagTh : flagEn} alt={selectedLang} style={{ width: "20px", height: "20px", borderRadius: "50%", objectFit: "cover" }} /> {selectedLang} ▾
            </button>
            {langOpen && (
              <div style={{ position: "absolute", top: "100%", left: 0, marginTop: "4px", backgroundColor: "#ffffff", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", overflow: "hidden", zIndex: 1300, minWidth: "80px" }}>
                {languages.map((lang) => (
                  <div key={lang} onClick={() => { setSelectedLang(lang); setLangOpen(false); onLangChange?.(lang); }} style={{ padding: "8px 12px", fontSize: "0.85rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", backgroundColor: lang === selectedLang ? "#f3f4f6" : "transparent" }}>
                    <img src={lang === "TH" ? flagTh : flagEn} alt={lang} style={{ width: "18px", height: "18px", borderRadius: "50%", objectFit: "cover" }} /> {lang}
                  </div>
                ))}
              </div>
            )}
          </div>`] : []),
    ...extraIconElements,
    ...extraButtonElements,
  ];
  if (ctaText) {
    const ctaFontEN = !/[\u0E00-\u0E7F]/.test(ctaText) ? `, fontFamily: "var(--font-family-en, var(--font-family))"` : "";
    rightItems.push(`<a href="${esc(ctaLink)}" onClick={(e) => handleClick(e, "${esc(ctaLink)}")} style={{ marginLeft: "8px", padding: "${navbarStyles.cta.desktop.padding}", background: "${esc(ctaBg)}", color: "#ffffff", borderRadius: "8px", textDecoration: "none", fontWeight: 600, fontSize: "0.875rem", whiteSpace: "nowrap"${ctaFontEN} }}>${escJsx(ctaText)}</a>`);
  }

  // Logo section: show both image and text when both exist
  let logoHtml = "";
  if (hasLogo) {
    const logoParts: string[] = [];
    if (logoUrl) {
      logoParts.push(`<img src=${logoSrc} alt="${esc(logoText || "Logo")}" style={{ height: "${navbarStyles.logo.desktop.height}px", width: "auto" }} />`);
    }
    if (logoText) {
      const logoFontEN = !/[\u0E00-\u0E7F]/.test(String(logoText)) ? `, fontFamily: "var(--font-family-en, var(--font-family))"` : "";
      logoParts.push(`<span style={{ fontSize: "1.25rem", fontWeight: 700${logoUrl ? `, marginLeft: "8px"` : ""}${logoFontEN} }}>${escJsx(logoText)}</span>`);
    }
    logoHtml = `<a href="/" style={{ display: "flex", alignItems: "center", textDecoration: "none", color: "inherit" }}>
          ${logoParts.join("\n          ")}
        </a>`;
  } else {
    logoHtml = `<div />`;
  }

  return `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
${imports.join("\n")}
${hasLangSelector ? `import flagTh from "../assets/flag-th.png";\nimport flagEn from "../assets/flag-en.png";` : ""}

interface ${sectionName}Props {
  lang?: "th" | "en";
  onNavigate?: (path: string) => void;
  onLangChange?: (lang: string) => void;
}

const ${sectionName}: React.FC<${sectionName}Props> = ({ lang = "th", onNavigate, onLangChange }) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(path);
    }
  };

  const navItems = ${navItemsJson};
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeHref, setActiveHref] = useState(navItems[0]?.href || "");${hasLangSelector ? `
  const [selectedLang, setSelectedLang] = useState(lang === "en" ? "EN" : "${escJsx(safeLanguages[0] || "TH")}");
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const languages = ${langJsonStr};
  useEffect(() => { setSelectedLang(lang === "en" ? "EN" : "${escJsx(safeLanguages[0] || "TH")}"); }, [lang]);` : ""}

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Track active section on scroll
  useEffect(() => {
    const sectionIds = navItems.map((item) => item.href).filter((h) => h.startsWith("#")).map((h) => h.slice(1));
    const scrollParent = document.querySelector("[class$=\\"-page\\"]")?.parentElement;
    const handleScroll = () => {
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionIds[i]);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveHref("#" + sectionIds[i]);
          return;
        }
      }
      if (sectionIds.length > 0) setActiveHref("#" + sectionIds[0]);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    if (scrollParent) {
      scrollParent.addEventListener("scroll", handleScroll, { passive: true });
    }
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollParent) {
        scrollParent.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);${hasLangSelector ? `

  // Close lang dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);` : ""}

  return (
    <nav style={{${isStickyNav ? ` position: "sticky", top: 0, zIndex: 1000,` : ""} backgroundColor: "${backgroundColor}", color: "${textColor}", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "${paddingY}px ${paddingX}px", maxWidth: "${maxWidth}", margin: "0 auto" }}>
        {/* Logo */}
        ${logoHtml}

        {/* Center Nav Items */}
        <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "center", gap: "8px" }} className="desktop-nav">
          {navItems.map((item, i) => {
            const label = typeof item.label === "object" ? (item.label as Record<string, string>)[lang] || (item.label as Record<string, string>).th : item.label;
            const labelFontFamily = /[\u0E00-\u0E7F]/.test(String(label)) ? undefined : "var(--font-family-en, var(--font-family))";
            return (
            <a key={i} href={item.href} onClick={(e) => { e.preventDefault(); setActiveHref(item.href); const el = document.getElementById(item.href.slice(1)); if (el) el.scrollIntoView({ behavior: "smooth" }); }} style={{ position: "relative", padding: "${navbarStyles.link.desktop.padding}", fontSize: "${navFontSize}px", ${navFfStyle} color: "inherit", textDecoration: "none", fontWeight: activeHref === item.href ? 700 : 500, cursor: "pointer", ...(labelFontFamily ? { fontFamily: labelFontFamily } : {}) }}>
              {label}
              <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, #fda0b8, #f9c26f)", borderRadius: "2px", transform: activeHref === item.href ? "scaleX(1)" : "scaleX(0)", transformOrigin: "center", transition: "transform 0.3s ease" }} />
            </a>
            );
          })}
        </div>

        {/* Right Items */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }} className="desktop-nav">
          ${rightItems.join("\n          ")}
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(true)}
          className="mobile-menu-btn"
          style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: "8px", color: "inherit" }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18" /></svg>
        </button>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          <div onClick={() => setMobileOpen(false)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9998 }} />
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "80%", maxWidth: "300px", backgroundColor: "${backgroundColor}", zIndex: 9999, boxShadow: "-4px 0 20px rgba(0,0,0,0.15)", overflowY: "auto", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "16px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
                <button onClick={() => setMobileOpen(false)} style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px", background: "none", border: "none", cursor: "pointer", color: "inherit" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {navItems.map((item, i) => {
                  const mLabel = typeof item.label === "object" ? (item.label as Record<string, string>)[lang] || (item.label as Record<string, string>).th : item.label;
                  const mFontFamily = /[\u0E00-\u0E7F]/.test(String(mLabel)) ? undefined : "var(--font-family-en, var(--font-family))";
                  return (
                    <a key={i} href={item.href} onClick={(e) => { e.preventDefault(); setMobileOpen(false); setActiveHref(item.href); const el = document.getElementById(item.href.slice(1)); if (el) el.scrollIntoView({ behavior: "smooth" }); }} style={{ display: "block", paddingTop: "12px", paddingBottom: "12px", fontWeight: activeHref === item.href ? 700 : 500, color: "inherit", textDecoration: "none", borderBottom: activeHref === item.href ? "3px solid" : "1px solid #e5e7eb", borderImage: activeHref === item.href ? "linear-gradient(90deg, #fda0b8, #f9c26f) 1" : "none", ...(mFontFamily ? { fontFamily: mFontFamily } : {}) }}>
                      {mLabel}
                    </a>
                  );
                })}
                ${mobileExtraButtonElements.join("\n                ")}
                ${ctaText && !ctaHideOnMobile ? `<a href="${esc(ctaLink)}" onClick={(e) => { setMobileOpen(false); handleClick(e, "${esc(ctaLink)}"); }} style={{ display: "block", padding: "8px 24px", background: "${ctaBackgroundColor ? esc(ctaBackgroundColor) : `linear-gradient(135deg, var(--color-primary, #3b82f6) 0%, var(--color-secondary, #10b981) 100%)`}", color: "#ffffff", borderRadius: "8px", textDecoration: "none", fontWeight: 600, textAlign: "center"${!/[\u0E00-\u0E7F]/.test(ctaText) ? `, fontFamily: "var(--font-family-en, var(--font-family))"` : ""} }}>${escJsx(ctaText)}</a>` : ""}
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default ${sectionName};
`;
};

/**
 * Generate a complete standalone TSX file for FlexNavbar
 */
export const generateFlexNavbarSection = (props: Props, childrenCode: string, sectionName: string): string => {
  const {
    backgroundColor = "#ffffff",
    textColor = "#1f2937",
    sticky = true,
    paddingX = 24,
    paddingY = 12,
    maxWidth = "1280px",
  } = props;

  return `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";

interface ${sectionName}Props {
  lang?: "th" | "en";
  onNavigate?: (path: string) => void;
  onLangChange?: (lang: string) => void;
}

const ${sectionName}: React.FC<${sectionName}Props> = ({ lang = "th", onNavigate, onLangChange }) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(path);
    }
  };

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <nav style={{${sticky ? ` position: "sticky", top: 0, zIndex: 1000,` : ""} backgroundColor: "${backgroundColor}", color: "${textColor}", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "${paddingY}px ${paddingX}px", maxWidth: "${maxWidth}", margin: "0 auto" }}>
        ${childrenCode}
        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(true)}
          className="mobile-menu-btn"
          style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: "8px", color: "inherit" }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18" /></svg>
        </button>
      </div>
      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          <div onClick={() => setMobileOpen(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9998 }} />
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(80vw, 300px)", backgroundColor: "${backgroundColor}", zIndex: 9999, padding: "24px", boxShadow: "-4px 0 20px rgba(0,0,0,0.15)", overflowY: "auto" }}>
            <button onClick={() => setMobileOpen(false)} style={{ position: "absolute", top: "16px", right: "16px", background: "none", border: "none", cursor: "pointer", fontSize: "1.5rem", color: "inherit" }}>&times;</button>
            <div style={{ marginTop: "48px", display: "flex", flexDirection: "column", gap: "8px" }}>
              ${childrenCode}
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default ${sectionName};
`;
};

/**
 * Generate HeroSlider with auto-play
 */
export const generateHeroSliderSection = (props: Props, sectionName: string, assets: ImageAsset[]): string => {
  const {
    slides = [],
    minHeight = 500,
    showDots = true,
    showArrows = true,
    autoplay = true,
    interval = 5000,
  } = props;

  const safeSlides = safeArray(slides);
  const slidesJson = JSON.stringify(
    safeSlides.map((s) => {
      const slide = s as Record<string, unknown>;
      return {
        title: slide.title || "",
        subtitle: slide.subtitle || slide.description || "",
        backgroundImage: slide.backgroundImage || "",
        buttonText: slide.buttonText || "",
        buttonLink: slide.buttonLink || "#",
      };
    }),
    null,
    2,
  );

  return `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";

const ${sectionName}: React.FC = () => {
  const [current, setCurrent] = useState(0);

  const slides = ${slidesJson};

  useEffect(() => {
    ${autoplay ? `const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, ${interval});
    return () => clearInterval(timer);` : "// Autoplay disabled"}
  }, [slides.length]);

  const prev = () => setCurrent((c) => (c - 1 + slides.length) % slides.length);
  const next = () => setCurrent((c) => (c + 1) % slides.length);

  return (
    <div style={{ position: "relative", width: "100%", minHeight: "${minHeight}px", overflow: "hidden" }}>
      {slides.map((slide, index) => (
        <div
          key={index}
          style={{
            position: index === 0 ? "relative" : "absolute",
            inset: 0,
            opacity: current === index ? 1 : 0,
            transition: "opacity 0.6s ease",
            minHeight: "${minHeight}px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundImage: slide.backgroundImage ? \`url(\${slide.backgroundImage})\` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.4)" }} />
          <div style={{ position: "relative", zIndex: 1, textAlign: "center", color: "#ffffff", padding: "24px" }}>
            {slide.title && <h2 style={{ fontSize: "${sliderStyles.title.desktop.fontSize}", fontWeight: 700, marginBottom: "16px" }}>{slide.title}</h2>}
            {slide.subtitle && <p style={{ fontSize: "${sliderStyles.subtitle.desktop.fontSize}", marginBottom: "24px", opacity: 0.9 }}>{slide.subtitle}</p>}
            {slide.buttonText && (
              <a href={slide.buttonLink} style={{ display: "inline-block", padding: "${sliderStyles.button.desktop.py}px ${sliderStyles.button.desktop.px}px", background: "var(--color-primary, #3b82f6)", color: "#ffffff", borderRadius: "8px", textDecoration: "none", fontWeight: 600, fontSize: "${sliderStyles.button.desktop.fontSize}" }}>
                {slide.buttonText}
              </a>
            )}
          </div>
        </div>
      ))}

      ${showArrows ? `{/* Arrows */}
      <button onClick={prev} style={{ position: "absolute", left: "${sliderStyles.arrow.desktop.position}px", top: "50%", transform: "translateY(-50%)", width: "${sliderStyles.arrow.desktop.size}px", height: "${sliderStyles.arrow.desktop.size}px", borderRadius: "50%", border: "none", background: "${sliderStyles.arrowBackground.default}", color: "#ffffff", cursor: "pointer", fontSize: "${sliderStyles.arrow.desktop.iconSize}px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>&#8249;</button>
      <button onClick={next} style={{ position: "absolute", right: "${sliderStyles.arrow.desktop.position}px", top: "50%", transform: "translateY(-50%)", width: "${sliderStyles.arrow.desktop.size}px", height: "${sliderStyles.arrow.desktop.size}px", borderRadius: "50%", border: "none", background: "${sliderStyles.arrowBackground.default}", color: "#ffffff", cursor: "pointer", fontSize: "${sliderStyles.arrow.desktop.iconSize}px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>&#8250;</button>` : ""}

      ${showDots ? `{/* Dots */}
      <div style={{ position: "absolute", bottom: "${sliderStyles.dot.desktop.bottom}px", left: "50%", transform: "translateX(-50%)", display: "flex", gap: "${sliderStyles.dot.desktop.gap}px", zIndex: 2 }}>
        {slides.map((_, i) => (
          <button key={i} onClick={() => setCurrent(i)} style={{ width: current === i ? "${sliderStyles.dot.desktop.activeWidth}px" : "${sliderStyles.dot.desktop.inactiveWidth}px", height: "${sliderStyles.dot.desktop.height}px", borderRadius: "${sliderStyles.dot.desktop.borderRadius}px", border: "none", background: current === i ? "${sliderStyles.dotBackground.active}" : "${sliderStyles.dotBackground.inactive}", cursor: "pointer", transition: "all 0.3s ease" }} />
        ))}
      </div>` : ""}
    </div>
  );
};

export default ${sectionName};
`;
};

/**
 * Generate BeforeAfter slider with drag interaction
 */
export const generateBeforeAfterSection = (props: Props, sectionName: string, assets: ImageAsset[]): string => {
  const {
    beforeImage = beforeAfterStyles.defaultImages.before,
    afterImage = beforeAfterStyles.defaultImages.after,
    beforeLabel = "Before",
    afterLabel = "After",
    initialPosition = 50,
    height,
    borderRadius = 12,
    aspectRatio = "landscape",
  } = props;

  const aspectRatios: Record<string, string> = {
    landscape: "56.25%",
    portrait: "133%",
    square: "100%",
    wide: "42.85%",
  };

  const usePaddingTop = !height;
  const paddingTop = aspectRatios[aspectRatio as string] || "56.25%";

  return `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useRef, useCallback } from "react";

const ${sectionName}: React.FC = () => {
  const [position, setPosition] = useState(${initialPosition});
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPosition((x / rect.width) * 100);
  }, []);

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e: React.MouseEvent) => { if (isDragging.current) updatePosition(e.clientX); };
  const handleTouchMove = (e: React.TouchEvent) => { updatePosition(e.touches[0].clientX); };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      style={{ position: "relative", ${usePaddingTop ? `paddingTop: "${paddingTop}",` : `height: "${height}px",`} borderRadius: "${borderRadius}px", overflow: "hidden", cursor: "ew-resize", userSelect: "none", maxWidth: "100%" }}
    >
      {/* After image (background) */}
      <img src=${imgRef(afterImage as string, assets)} alt="${esc(afterLabel)}" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />

      {/* Before image (clipped) */}
      <img src=${imgRef(beforeImage as string, assets)} alt="${esc(beforeLabel)}" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", clipPath: \`inset(0 \${100 - position}% 0 0)\` }} />

      {/* Slider line */}
      <div
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        style={{ position: "absolute", top: 0, bottom: 0, left: \`\${position}%\`, transform: "translateX(-50%)", width: "${beforeAfterStyles.slider.lineWidth}px", backgroundColor: "#ffffff", cursor: "ew-resize", zIndex: 2 }}
      >
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "${beforeAfterStyles.slider.handleSize}px", height: "${beforeAfterStyles.slider.handleSize}px", borderRadius: "50%", backgroundColor: "#ffffff", boxShadow: "0 2px 6px rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>
          &#8596;
        </div>
      </div>

      {/* Labels */}
      <span style={{ position: "absolute", top: "${beforeAfterStyles.label.top}px", left: "${beforeAfterStyles.label.horizontalOffset}px", padding: "${beforeAfterStyles.label.padding.py}px ${beforeAfterStyles.label.padding.px}px", backgroundColor: "${beforeAfterStyles.label.backgroundColor}", color: "#ffffff", borderRadius: "${beforeAfterStyles.label.borderRadius}px", fontSize: "${beforeAfterStyles.label.fontSize}", zIndex: 3 }}>${escJsx(beforeLabel)}</span>
      <span style={{ position: "absolute", top: "${beforeAfterStyles.label.top}px", right: "${beforeAfterStyles.label.horizontalOffset}px", padding: "${beforeAfterStyles.label.padding.py}px ${beforeAfterStyles.label.padding.px}px", backgroundColor: "${beforeAfterStyles.label.backgroundColor}", color: "#ffffff", borderRadius: "${beforeAfterStyles.label.borderRadius}px", fontSize: "${beforeAfterStyles.label.fontSize}", zIndex: 3 }}>${escJsx(afterLabel)}</span>
    </div>
  );
};

export default ${sectionName};
`;
};

/**
 * Generate Gallery with lightbox
 */
export const generateGallerySection = (props: Props, sectionName: string, assets: ImageAsset[]): string => {
  const {
    images = [],
    columns = 3,
    gap = 16,
    borderRadius = 8,
    enableLightbox = true,
  } = props;

  const safeImages = safeArray(images);
  const imagesJson = JSON.stringify(
    safeImages.map((img) => {
      const image = img as Record<string, unknown>;
      return { src: image.src || "", alt: image.alt || "", caption: image.caption || "" };
    }),
    null,
    2,
  );

  return `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState } from "react";

const ${sectionName}: React.FC = () => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const images = ${imagesJson};

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(${columns}, 1fr)", gap: "${gap}px" }}>
        {images.map((img, index) => (
          <div
            key={index}
            ${enableLightbox ? `onClick={() => setLightboxIndex(index)}` : ""}
            style={{ aspectRatio: "1", overflow: "hidden", borderRadius: "${borderRadius}px", cursor: ${enableLightbox ? '"pointer"' : '"default"'} }}
          >
            <img src={img.src} alt={img.alt} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s ease" }} />
          </div>
        ))}
      </div>

      ${enableLightbox ? `{/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          onClick={() => setLightboxIndex(null)}
          style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.9)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <button onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev! - 1 + images.length) % images.length); }} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: "2rem", cursor: "pointer", width: "48px", height: "48px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>&#8249;</button>
          <img src={images[lightboxIndex].src} alt={images[lightboxIndex].alt} style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: "8px" }} onClick={(e) => e.stopPropagation()} />
          <button onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev! + 1) % images.length); }} style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: "2rem", cursor: "pointer", width: "48px", height: "48px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>&#8250;</button>
          <button onClick={() => setLightboxIndex(null)} style={{ position: "absolute", top: "16px", right: "16px", background: "none", border: "none", color: "#fff", fontSize: "2rem", cursor: "pointer" }}>&times;</button>
        </div>
      )}` : ""}
    </>
  );
};

export default ${sectionName};
`;
};

/**
 * Generate FilterableFeatures with tab filtering
 */
export const generateFilterableFeaturesSection = (props: Props, childrenCode: string, sectionName: string): string => {
  const {
    categories = [],
    activeTabColor = "#E8834A",
    categoryColors = {},
    cardsPerRow = 3,
    gap = 24,
    paddingTop = 32,
    backgroundColor = "transparent",
    textColor = "#1f2937",
    paddingX = 24,
    paddingBottom = 32,
    maxWidth: maxWidthProp = 1280,
  } = props;

  const safeCategories = safeArray(categories);
  const categoriesJson = JSON.stringify(safeCategories);
  const catColorsObj = categoryColors as Record<string, string>;
  const categoryColorsJson = JSON.stringify(catColorsObj);

  return `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useMemo } from "react";

const ${sectionName}: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const [currentPage, setCurrentPage] = useState(0);
  const categories = ${categoriesJson};
  const categoryColors: Record<string, string> = ${categoryColorsJson};
  const cardsPerRow = ${cardsPerRow};
  const rowsPerPage = 2;
  const itemsPerPage = cardsPerRow * rowsPerPage;

  const allCards: { category: string; element: React.ReactNode }[] = ${childrenCode};

  const filtered = useMemo(() => {
    return activeCategory === "All" ? allCards : allCards.filter((c) => c.category === activeCategory);
  }, [activeCategory]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const safePage = Math.min(currentPage, Math.max(0, totalPages - 1));
  const paged = filtered.slice(safePage * itemsPerPage, (safePage + 1) * itemsPerPage);
  const needsPagination = filtered.length > itemsPerPage;

  const handleTabClick = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(0);
  };

  const arrowStyle = (disabled: boolean): React.CSSProperties => ({
    width: "36px", height: "36px", borderRadius: "50%", border: "1px solid #d1d5db",
    backgroundColor: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center",
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.3 : 1, flexShrink: 0,
  });

  return (
    <div style={{ backgroundColor: "${backgroundColor}", color: "${textColor}", maxWidth: "${typeof maxWidthProp === "string" ? maxWidthProp : (maxWidthProp ? `${maxWidthProp}px` : "100%")}", width: "100%", margin: "0 auto", padding: "${paddingTop}px ${paddingX}px ${paddingBottom}px ${paddingX}px", boxSizing: "border-box" }} className="mobile-full-width">
      {/* Tabs + Pagination arrows */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: "24px", gap: "8px" }}>
        <div style={{ display: "flex", gap: "8px", flex: 1, overflowX: "auto", scrollbarWidth: "none", flexWrap: "nowrap" }}>
          <button
            onClick={() => handleTabClick("All")}
            style={{
              padding: "6px 16px", borderRadius: "999px",
              border: activeCategory === "All" ? "none" : "1px solid #d1d5db",
              cursor: "pointer", fontWeight: 600, fontSize: "0.875rem",
              whiteSpace: "nowrap", flexShrink: 0,
              backgroundColor: activeCategory === "All" ? "${activeTabColor}" : "#ffffff",
              color: activeCategory === "All" ? "#ffffff" : "${textColor}",
              transition: "all 0.15s", fontFamily: "inherit", outline: "none",
            }}
          >
            All
          </button>
          {categories.map((cat, i) => {
            const isActive = activeCategory === cat;
            const catColor = categoryColors[cat] || "${activeTabColor}";
            return (
              <button
                key={i}
                onClick={() => handleTabClick(cat)}
                style={{
                  padding: "6px 16px", borderRadius: "999px",
                  border: isActive ? "none" : "1px solid #d1d5db",
                  cursor: "pointer", fontWeight: 500, fontSize: "0.875rem",
                  whiteSpace: "nowrap", flexShrink: 0,
                  backgroundColor: isActive ? catColor : "#ffffff",
                  color: isActive ? "#ffffff" : "${textColor}",
                  transition: "all 0.15s", fontFamily: "inherit", outline: "none",
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>
        {needsPagination && (
          <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
            <button onClick={() => setCurrentPage((p) => Math.max(0, p - 1))} disabled={safePage <= 0} style={arrowStyle(safePage <= 0)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} style={arrowStyle(safePage >= totalPages - 1)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        )}
      </div>

      {/* Cards grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(${cardsPerRow}, 1fr)", gap: "${gap}px" }}>
        {paged.map((card, i) => <React.Fragment key={i}>{card.element}</React.Fragment>)}
      </div>
    </div>
  );
};

export default ${sectionName};
`;
};

// ─────────────────────────────────────────────
// Hero (simple, no hooks)
// ─────────────────────────────────────────────

export const generateHero = (props: Props, assets: ImageAsset[], childrenCode = ""): string => {
  const {
    title = "Welcome",
    subtitle = "",
    backgroundImage = "",
    backgroundPosition = "center",
    backgroundZoom = 100,
    backgroundColor = "#1e3a5f",
    overlayColor = "#000000",
    overlayOpacity = 0.4,
    minHeight = 500,
    buttonText = "",
    buttonLink = "#",
    buttonVariant = "primary",
    secondaryButtonText = "",
    secondaryButtonLink = "#",
    secondaryButtonVariant = "outline",
    textAlign = "center",
    titleColor = "#ffffff",
    subtitleColor = "#ffffff",
    textFontFamily,
  } = props;

  let bgImgStyle = "";
  if (backgroundImage) {
    const bgRef = getImageReference(backgroundImage as string, assets);
    const bgSizeCss = backgroundZoom && Number(backgroundZoom) !== 100 ? `${backgroundZoom}%` : "cover";
    if (bgRef.type === "import") {
      bgImgStyle = `, backgroundImage: \`url(\${${bgRef.value}})\`, backgroundSize: "${bgSizeCss}", backgroundPosition: "${backgroundPosition}"`;
    } else {
      bgImgStyle = `, backgroundImage: "url(${esc(bgRef.value)})", backgroundSize: "${bgSizeCss}", backgroundPosition: "${backgroundPosition}"`;
    }
  }

  const buttons: string[] = [];
  if (buttonText) {
    const btnFont = !/[\u0E00-\u0E7F]/.test(resolveText(String(buttonText))) ? `, fontFamily: "var(--font-family-en, var(--font-family))"` : "";
    buttons.push(`<a href="${esc(buttonLink)}" style={{ display: "inline-block", padding: "${heroStyles.button.desktop.py}px ${heroStyles.button.desktop.px}px", background: "linear-gradient(135deg, var(--color-primary, #3b82f6) 0%, var(--color-secondary, #10b981) 100%)", color: "#ffffff", borderRadius: "8px", textDecoration: "none", fontWeight: 500, fontSize: "1rem"${btnFont} }}>${escJsx(buttonText)}</a>`);
  }
  if (secondaryButtonText) {
    const secStyle = secondaryButtonVariant === "outline"
      ? `background: "transparent", color: "#ffffff", border: "2px solid #ffffff"`
      : `background: "var(--color-secondary, #10b981)", color: "#ffffff", border: "none"`;
    const secBtnFont = !/[\u0E00-\u0E7F]/.test(resolveText(String(secondaryButtonText))) ? `, fontFamily: "var(--font-family-en, var(--font-family))"` : "";
    buttons.push(`<a href="${esc(secondaryButtonLink)}" style={{ display: "inline-block", padding: "${heroStyles.button.desktop.py}px ${heroStyles.button.desktop.px}px", ${secStyle}, borderRadius: "8px", textDecoration: "none", fontWeight: 500, fontSize: "1rem"${secBtnFont} }}>${escJsx(secondaryButtonText)}</a>`);
  }

  // Handle gradient title/subtitle colors
  const titleColorStr = String(titleColor);
  const isGradientTitle = titleColorStr.includes("gradient");
  const titleStyle = isGradientTitle
    ? `background: "${esc(titleColorStr)}", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"`
    : `color: "${esc(titleColorStr)}"`;
  const subtitleColorStr = String(subtitleColor);
  const isGradientSubtitle = subtitleColorStr.includes("gradient");
  const subtitleStyle = isGradientSubtitle
    ? `background: "${esc(subtitleColorStr)}", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"`
    : `color: "${esc(subtitleColorStr)}"`;

  // If childrenCode exists (linked Text/Button nodes), use it instead of props-based text
  const thaiRe = /[\u0E00-\u0E7F]/;
  const getFontStyle = (text: string) => {
    if (textFontFamily) return `, fontFamily: "'${esc(textFontFamily)}', var(--font-family), sans-serif"`;
    if (!thaiRe.test(resolveText(text))) return `, fontFamily: "var(--font-family-en, var(--font-family))"`;
    return "";
  };
  const contentCode = childrenCode
    ? childrenCode
    : `<h1 style={{ fontSize: "${heroStyles.title.desktop.fontSize}", fontWeight: ${heroStyles.title.desktop.fontWeight}, ${titleStyle}, marginBottom: "16px"${getFontStyle(String(title))} }}>${escJsx(title)}</h1>
    ${subtitle ? `<p style={{ fontSize: "${heroStyles.subtitle.desktop.fontSize}", ${subtitleStyle}, opacity: 0.9, marginBottom: "32px"${getFontStyle(String(subtitle))} }}>${escJsx(subtitle)}</p>` : ""}
    ${buttons.length > 0 ? `<div style={{ display: "flex", gap: "16px", justifyContent: "${textAlign === "left" ? "flex-start" : textAlign === "right" ? "flex-end" : "center"}" }}>\n      ${buttons.join("\n      ")}\n    </div>` : ""}`;

  return `<div style={{ position: "relative", minHeight: "${minHeight}px", backgroundColor: "${backgroundColor}", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden"${bgImgStyle} }}>
  ${backgroundImage ? `<div style={{ position: "absolute", inset: 0, backgroundColor: "${esc(overlayColor)}", opacity: ${overlayOpacity} }} />` : ""}
  <div style={{ position: "relative", zIndex: 1, textAlign: "${textAlign}", padding: "${heroStyles.padding.desktop.py}px ${heroStyles.padding.desktop.px}px", maxWidth: "56rem" }}>
    ${contentCode}
  </div>
</div>`;
};

// ─────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────

export const generateFooter = (props: Props, assets: ImageAsset[], childrenCode = ""): string => {
  const {
    clinicName = "Clinic",
    description = "",
    address = "",
    phone = "",
    email = "",
    columns = [],
    socialLinks = {},
    backgroundColor = "#1f2937",
    textColor = "#ffffff",
    logoUrl = "",
    copyrightText = "",
    borderRadius = 0,
    padding = 48,
    textFontFamily,
  } = props;

  const safeColumns = safeArray(columns);
  const social = socialLinks as Record<string, string>;

  // New footer props (set from the settings panel / template)
  const fp = props as Record<string, unknown>;
  const columnAlign = (fp.columnAlign as string) || "left";
  const alignRight = columnAlign === "right";
  const showClinicName = fp.showClinicName !== false;
  const showContactIcons = fp.showContactIcons !== false;
  const legalLinks = safeArray(fp.legalLinks).filter((l) => {
    const x = l as Record<string, unknown>;
    return x.label && x.href;
  });

  const socialIconMap: Record<string, string> = {
    facebook: "/static/images/social/facebook.png",
    instagram: "/static/images/social/ig.png",
    line: "/static/images/social/line.png",
    youtube: "/static/images/social/youtube.png",
    tiktok: "/static/images/social/tiktok.png",
    x: "/static/images/social/twitter.png",
    linkedin: "/static/images/social/linked.png",
  };

  const socialItems = Object.entries(social)
    .filter(([, url]) => url)
    .map(
      ([platform, url]) => {
        const iconSrc = socialIconMap[platform] || "";
        const iconImg = iconSrc
          ? `<img src="${iconSrc}" alt="${platform}" style={{ width: "24px", height: "24px", objectFit: "contain" }} />`
          : `${platform.charAt(0).toUpperCase() + platform.slice(1)}`;
        return `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center" }}>${iconImg}</a>`;
      },
    );

  const columnsSections = safeColumns
    .map((col) => {
      const c = col as Record<string, unknown>;
      const title = (c.title || "") as string;
      const links = safeArray(c.links);
      const linkItems = links
        .map((l) => {
          const link = l as Record<string, unknown>;
          const href = String(link.href || "#");
          const label = escJsx(link.label || "");
          // Auto-detect social icons from URL
          let iconImg = "";
          if (href.includes("facebook")) {
            iconImg = `<img src="/static/images/social/facebook.png" alt="Facebook" style={{ width: "16px", height: "16px" }} /> `;
          } else if (href.includes("line.me") || href.includes("lin.ee")) {
            iconImg = `<img src="/static/images/social/line.png" alt="LINE" style={{ width: "16px", height: "16px" }} /> `;
          }
          const linkFP = fontENProp(link.label);
          const aStyle = alignRight
            ? `display: "inline-flex", flexDirection: "row-reverse", alignItems: "center", gap: "8px", color: "inherit", opacity: 0.7, textDecoration: "none", fontSize: "${footerStyles.fontSize.text.desktop}", marginBottom: "8px"`
            : `display: "flex", alignItems: "center", gap: "8px", color: "inherit", opacity: 0.7, textDecoration: "none", fontSize: "${footerStyles.fontSize.text.desktop}", marginBottom: "8px"`;
          return `<a href="${esc(href)}" style={{ ${aStyle} }}>${iconImg}${linkFP ? `<span style={{ ${linkFP} }}>${label}</span>` : `<span>${label}</span>`}</a>`;
        })
        .join("\n        ");
      const colStyle = alignRight ? ` style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" }}` : "";
      return `<div${colStyle}>
      <h4 style={{ fontSize: "${footerStyles.fontSize.columnTitle.desktop}", fontWeight: 600, marginBottom: "16px", opacity: 0.9${fontENStyle(title)} }}>${escJsx(title)}</h4>
      ${linkItems}
    </div>`;
    })
    .join("\n    ");

  const copyright = copyrightText || `\u00a9 ${new Date().getFullYear()} ${clinicName as string}. All rights reserved.`;

  const legalContent = legalLinks.length > 0
    ? `<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", marginBottom: "16px", fontSize: "${footerStyles.fontSize.text.desktop}", opacity: 0.8 }}>${legalLinks.map((l, i) => {
        const x = l as Record<string, unknown>;
        const a = `<a href="${esc(String(x.href))}" style={{ color: "inherit", textDecoration: "none"${fontENStyle(x.label)} }}>${escJsx(x.label)}</a>`;
        return i < legalLinks.length - 1 ? `${a}<span style={{ opacity: 0.4 }}>|</span>` : a;
      }).join("")}</div>`
    : "";
  const pin = showContactIcons ? `{"\ud83d\udccd"} ` : "";
  const tel = showContactIcons ? `{"\ud83d\udcde"} ` : "";
  const mail = showContactIcons ? `{"\u2709\ufe0f"} ` : "";

  const clinicInfoContent = childrenCode || `${showClinicName ? `<h3 style={{ fontSize: "${footerStyles.fontSize.clinicName.desktop}", fontWeight: 700, marginBottom: "16px"${fontENStyle(clinicName, textFontFamily)} }}>${escJsx(clinicName)}</h3>` : ""}
      ${description ? `<p style={{ fontSize: "${footerStyles.fontSize.text.desktop}", opacity: 0.7, lineHeight: 1.6, marginBottom: "16px"${fontENStyle(description, textFontFamily)} }}>${escJsx(description)}</p>` : ""}
      ${legalContent}
      ${address ? `<p style={{ fontSize: "${footerStyles.fontSize.text.desktop}", opacity: 0.7, marginBottom: "8px", lineHeight: 1.6${fontENStyle(address)} }}>${pin}${escJsx(address)}</p>` : ""}
      ${phone ? `<p style={{ fontSize: "${footerStyles.fontSize.text.desktop}", opacity: 0.7, marginBottom: "8px"${fontENStyle(phone)} }}>${tel}${escJsx(phone)}</p>` : ""}
      ${email ? `<p style={{ fontSize: "${footerStyles.fontSize.text.desktop}", opacity: 0.7, marginBottom: "8px"${fontENStyle(email)} }}>${mail}${escJsx(email)}</p>` : ""}
      ${socialItems.length > 0 ? `<div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>\n        ${socialItems.join("\n        ")}\n      </div>` : ""}`;

  return `<footer style={{ backgroundColor: "${backgroundColor}", color: "${textColor}", padding: "${padding}px 24px", borderRadius: "${borderRadius}px" }}>
  <div style={{ maxWidth: "1280px", margin: "0 auto", display: "grid", gridTemplateColumns: "${alignRight ? "1fr auto" : footerStyles.grid.desktop}", gap: "32px", alignItems: "start" }}>
    {/* Clinic Info */}
    <div>
      ${logoUrl ? `<img src=${imgRef(logoUrl as string, assets)} alt="${esc(clinicName)}" style={{ height: "60px", marginBottom: "16px", display: "block" }} />` : ""}
      ${clinicInfoContent}
    </div>
    ${columnsSections}
  </div>
  <div style={{ maxWidth: "1280px", margin: "32px auto 0", paddingTop: "24px", borderTop: "1px solid rgba(156, 163, 175, 0.4)", textAlign: "center", fontSize: "${footerStyles.fontSize.text.desktop}", opacity: 0.5${fontENStyle(copyright)} }}>
    ${escJsx(copyright)}
  </div>
</footer>`;
};

// ─────────────────────────────────────────────
// Nav sub-components (inline within FlexNavbar)
// ─────────────────────────────────────────────

export const generateNavLogo = (props: Props, assets: ImageAsset[]): string => {
  const {
    logoUrl = "",
    logoText = "Clinic",
    height = 40,
    fontSize = 20,
    linkUrl = "/",
  } = props;

  if (logoUrl) {
    return `<a href="${esc(linkUrl)}" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}><img src=${imgRef(logoUrl as string, assets)} alt="Logo" style={{ height: "${height}px", width: "auto" }} /></a>`;
  }
  return `<a href="${esc(linkUrl)}" style={{ fontSize: "${fontSize}px", fontWeight: 700, color: "inherit", textDecoration: "none", flexShrink: 0 }}>${escJsx(logoText)}</a>`;
};

export const generateNavMenu = (props: Props): string => {
  const {
    items = [],
    gap = 24,
    fontSize = 14,
    fontWeight = 500,
  } = props;

  const safeItems = safeArray(items);
  const links = safeItems
    .map((n) => {
      const item = n as Record<string, unknown>;
      const label = item.label;
      const labelJsx = (typeof label === "object" && label !== null)
        ? `{lang === "en" ? "${escJsx((label as Record<string, string>).en || "")}" : "${escJsx((label as Record<string, string>).th || "")}"}`
        : escJsx(label || "");
      return `<a href="${esc(item.href || "#")}" style={{ fontSize: "${fontSize}px", fontWeight: ${fontWeight}, color: "inherit", textDecoration: "none", opacity: 0.8, whiteSpace: "nowrap" }}>${labelJsx}</a>`;
    })
    .join("\n    ");

  return `<div style={{ display: "flex", alignItems: "center", gap: "${gap}px", marginLeft: "auto" }} className="desktop-nav">\n    ${links}\n  </div>`;
};

export const generateNavCTA = (props: Props): string => {
  const {
    text = "Book Now",
    link = "#",
    backgroundColor = "var(--color-primary, #3b82f6)",
    textColor = "#ffffff",
    borderRadius = 8,
    paddingX = 20,
    paddingY = 10,
  } = props;

  return `<a href="${esc(link)}" style={{ display: "inline-block", padding: "${paddingY}px ${paddingX}px", backgroundColor: "${backgroundColor}", color: "${textColor}", borderRadius: "${borderRadius}px", textDecoration: "none", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0, fontSize: "0.875rem" }}>${escJsx(text)}</a>`;
};

export const generateNavbarContainer = (props: Props, childrenCode: string): string => {
  const {
    backgroundColor = "#ffffff",
    paddingX = 24,
    paddingY = 12,
    sticky = true,
    maxWidth,
  } = props;

  return `<nav style={{${sticky ? ` position: "sticky", top: 0, zIndex: 1000,` : ""} backgroundColor: "${backgroundColor}", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", padding: "${paddingY}px ${paddingX}px", maxWidth: "${maxWidth || "1280px"}", margin: "0 auto", minHeight: "40px" }}>
    ${childrenCode}
  </div>
</nav>`;
};
